import { z } from 'zod';
// Auth validation schemas
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    tenantId: z.string().uuid().optional(),
});
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password is too long'),
    name: z.string().min(2, 'Name must be at least 2 characters'),
});
/**
 * Body for an invited user creating their account directly from an invite link.
 * The email is taken from the (already-verified) invite token, never from the
 * request body — so an invitee can't bind the new account to a different email.
 */
export const acceptInvitationRegisterSchema = z.object({
    name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100),
    password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password is too long'),
});
/** Super-admin grant of a complimentary (free) license to a tenant. */
export const grantComplimentarySchema = z.object({
    planTier: z.number().int().min(0).max(5).optional(),
    seatCount: z.number().int().min(1, 'At least one seat').max(1000),
    reason: z.string().trim().max(500).optional(),
    expiresAt: z.string().datetime().optional(),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
// BOQ validation schemas
export const billItemCreateSchema = z.object({
    jobId: z.string().uuid(),
    catalogueItemId: z.string().uuid().optional(),
    costCentreId: z.string().uuid(),
    bLoad: z.number().int().positive().optional().default(1),
    quantity: z.number().optional().default(0),
    quantityFormula: z.string().max(500).optional(),
    unitPrice: z.number().optional().default(0),
    workup: z.string().optional(),
    supplierId: z.string().uuid().optional(),
});
export const billItemUpdateSchema = z.object({
    quantity: z.number().optional(),
    quantityFormula: z.string().max(500).optional(),
    unitPrice: z.number().optional(),
    workup: z.string().optional(),
    supplierId: z.string().uuid().nullable().optional(),
    costCentreId: z.string().uuid().optional(),
    bLoad: z.number().int().positive().optional(),
});
export const repriceOptionsSchema = z.object({
    costCentre: z.string().optional(),
    bLoad: z.number().int().positive().optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    scope: z.enum(['all', 'costCentre', 'load', 'selected']).optional(),
});
export const recipeExplosionSchema = z.object({
    quantity: z.number().positive(),
    variableValues: z.record(z.string(), z.number()).optional().default({}),
    /** When set, explode the assembly override stored on this bill line instead of the saved catalogue assembly. */
    billItemId: z.string().uuid().optional(),
    options: z.object({
        removeParent: z.boolean().optional().default(false),
        explodeRecipes: z.boolean().optional().default(true),
        maxDepth: z.number().int().min(1).max(10).optional(),
    }).optional(),
});
// Assembly (recipe) override on a BOQ line — editable copy of the assembly's items
// stored in bill_items.metadata.assemblyOverride. "Assembly" / "Assembly Items" is the
// user-facing name for the catalogue recipe / recipe_components layer underneath.
export const assemblyItemSchema = z.object({
    /** Catalogue price code, or null for an ad-hoc (free-text) item. */
    priceCode: z.string().max(50).nullable().optional(),
    description: z.string().min(1).max(500),
    quantity: z.number().finite(),
    unitPrice: z.number().finite().min(0),
    unitCode: z.string().max(50).nullable().optional(),
    unitDisplay: z.string().max(50).nullable().optional(),
    costCentreCode: z.string().max(50).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    adhoc: z.boolean().optional().default(false),
    /** Quantity formula ({qty}/variables) carried from the assembly — see AssemblyLineItem. */
    formula: z.string().max(500).nullable().optional(),
});
export const assemblyItemsSaveSchema = z.object({
    /** Line-local display name for the assembly; null/omitted ⇒ keep the catalogue description. */
    name: z.string().max(500).nullable().optional(),
    items: z.array(assemblyItemSchema).max(200),
    /** Assembly variable values for this line (e.g. { perimeter: 40 }), fed into item formulas. */
    variables: z.record(z.string().regex(/^\w{1,40}$/), z.number().finite()).optional(),
});
export const saveLineAsAssemblySchema = z.object({
    name: z.string().min(1).max(500),
});
// Write a line's edited items back to its EXISTING catalogue assembly (name optional — keep current).
export const updateAssemblySchema = z.object({
    name: z.string().max(500).nullable().optional(),
});
// (the strict catalogue item schemas that used to live here moved to the module-review #5 block below)
export const priceCreateSchema = z.object({
    price: z.number(),
    effectiveDate: z.string().or(z.date()).optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    isManualOverride: z.boolean().optional().default(false),
    changeReason: z.string().max(500).optional(),
});
// (the strict job schemas that used to live here moved to the module-review #6 block below)
// Order validation schemas
// Order create/update schemas live with the other purchase-order schemas further down (module pass 3) —
// the earlier strict versions here were never wired to the route.
// Supplier create/update schemas live with the RFQ schemas further down (module pass 2) — the
// earlier strict versions here were never wired to a route and rejected the SPA's real payloads.
export const supplierPriceCreateSchema = z.object({
    priceCode: z.string().min(1).max(50),
    supplierReference: z.string().max(100).optional(),
    price: z.number(),
    effectiveDate: z.string().or(z.date()).optional(),
    expiryDate: z.string().or(z.date()).optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    area: z.string().max(50).optional(),
    comments: z.string().max(500).optional(),
});
// Quote / Proposal validation schemas
export const createQuoteSchema = z.object({
    jobId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    description: z.string().max(500).optional(),
    siteAddress: z.string().max(500).optional(),
    siteSuburb: z.string().max(100).optional(),
    siteState: z.string().max(50).optional(),
    sitePostcode: z.string().max(20).optional(),
    validUntil: z.string().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    coverLetter: z.string().optional(),
    termsConditions: z.string().optional(),
    taxRate: z.number().min(0).max(100).optional(),
    depositType: z.enum(['percentage', 'fixed', 'none']).optional(),
    depositValue: z.number().min(0).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(200).optional(),
    fromTemplateId: z.string().uuid().optional(),
});
export const updateQuoteSchema = z.object({
    description: z.string().max(500).optional(),
    contactId: z.string().uuid().nullable().optional(),
    jobId: z.string().uuid().nullable().optional(),
    siteAddress: z.string().max(500).optional(),
    siteSuburb: z.string().max(100).optional(),
    siteState: z.string().max(50).optional(),
    sitePostcode: z.string().max(20).optional(),
    validUntil: z.string().nullable().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    coverLetter: z.string().optional(),
    termsConditions: z.string().optional(),
    taxRate: z.number().min(0).max(100).optional(),
    depositType: z.enum(['percentage', 'fixed', 'none']).optional(),
    depositValue: z.number().min(0).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(200).optional(),
});
export const createQuoteItemSchema = z.object({
    lineType: z.enum(['product', 'heading', 'subtotal', 'comment', 'assembly', 'assembly_item', 'running_subtotal']).optional().default('product'),
    parentItemId: z.string().uuid().optional(),
    catalogueItemId: z.string().uuid().optional(),
    priceCode: z.string().max(50).optional(),
    description: z.string().min(1).max(2000),
    quantity: z.number().optional().default(1),
    unitId: z.string().uuid().optional(),
    unitDisplay: z.string().max(50).optional(),
    unitCost: z.number().optional().default(0),
    markupPercent: z.number().optional().default(0),
    unitPrice: z.number().optional().default(0),
    costCentreId: z.string().uuid().optional(),
    assemblyId: z.string().max(100).optional(),
    isVisible: z.boolean().optional().default(true),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
});
export const updateQuoteItemSchema = z.object({
    lineType: z.enum(['product', 'heading', 'subtotal', 'comment', 'assembly', 'assembly_item', 'running_subtotal']).optional(),
    parentItemId: z.string().uuid().nullable().optional(),
    catalogueItemId: z.string().uuid().nullable().optional(),
    priceCode: z.string().max(50).optional(),
    description: z.string().min(1).max(2000).optional(),
    quantity: z.number().optional(),
    unitId: z.string().uuid().nullable().optional(),
    unitDisplay: z.string().max(50).optional(),
    unitCost: z.number().optional(),
    markupPercent: z.number().optional(),
    unitPrice: z.number().optional(),
    costCentreId: z.string().uuid().nullable().optional(),
    assemblyId: z.string().max(100).nullable().optional(),
    isVisible: z.boolean().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const reorderQuoteItemsSchema = z.object({
    items: z.array(z.object({
        id: z.string().uuid(),
        sortOrder: z.number().int().min(0),
        parentItemId: z.string().uuid().nullable().optional(),
    })),
});
// Variation Order schemas
export const createVariationOrderSchema = z.object({
    jobId: z.string().uuid(),
    description: z.string().max(500).optional(),
    additionalText: z.string().optional(),
    mode: z.enum(['simple', 'detailed']).optional().default('simple'),
    markupPercent: z.number().min(0).max(100).optional().default(0),
});
export const updateVariationOrderSchema = z.object({
    description: z.string().max(500).optional(),
    additionalText: z.string().optional(),
    mode: z.enum(['simple', 'detailed']).optional(),
    markupPercent: z.number().min(0).max(100).optional(),
});
export const createVariationLineSchema = z.object({
    description: z.string().min(1).max(500),
    lineType: z.enum(['debit', 'credit']).optional().default('debit'),
    costCentreId: z.string().uuid().optional(),
    catalogueItemId: z.string().uuid().optional(),
    unitId: z.string().uuid().optional(),
    unitDisplay: z.string().max(50).optional(),
    quantity: z.number().optional().default(1),
    unitPrice: z.number().optional().default(0),
    amountExGst: z.number().optional().default(0),
    notes: z.string().optional(),
});
export const updateVariationLineSchema = z.object({
    description: z.string().min(1).max(500).optional(),
    lineType: z.enum(['debit', 'credit']).optional(),
    costCentreId: z.string().uuid().nullable().optional(),
    catalogueItemId: z.string().uuid().nullable().optional(),
    unitId: z.string().uuid().nullable().optional(),
    unitDisplay: z.string().max(50).optional(),
    quantity: z.number().optional(),
    unitPrice: z.number().optional(),
    amountExGst: z.number().optional(),
    notes: z.string().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
// ============================================================
// ALLOWANCES (Provisional Sums / Prime Cost)
// ============================================================
export const allowanceTypeSchema = z.enum(['PS', 'PC']);
/** Margin must be 0 ≤ x < 100 (100 would mean infinite client price under true margin). */
export const allowanceMarginSchema = z.number().min(0).lt(100);
export const markAllowanceSchema = z.object({
    type: allowanceTypeSchema,
    notes: z.string().max(2000).optional(),
});
export const updateAllowanceSchema = z.object({
    type: allowanceTypeSchema.optional(),
    notes: z.string().max(2000).optional(),
    /** Override the job-level default margin for this line. Pass null to clear. */
    marginPercent: allowanceMarginSchema.nullable().optional(),
});
/** PATCH /api/allowances/jobs/:jobId/default-margin */
export const setJobAllowanceMarginSchema = z.object({
    marginPercent: allowanceMarginSchema,
});
/** POST /api/allowances/jobs/:jobId/adhoc — create a new ad-hoc allowance line */
export const createAdhocAllowanceSchema = z.object({
    type: allowanceTypeSchema,
    description: z.string().min(1).max(500),
    amountExGst: z.number().min(0),
    costCentreId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    marginPercent: allowanceMarginSchema.optional(),
});
/** POST /api/allowances/bill-items/:childId/allocate — link a child bill item to an allowance parent */
export const allocateBillItemSchema = z.object({
    /** UUID of the allowance bill_item this child belongs to. Pass null to clear. */
    allowanceBillItemId: z.string().uuid().nullable(),
});
export const recordAllowanceReconciliationSchema = z.object({
    actualCostExGst: z.number(),
    sourceType: z.enum(['manual', 'order', 'invoice', 'variation']).optional().default('manual'),
    sourceId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    recordedAt: z.string().or(z.date()).optional(),
});
export const updateAllowanceReconciliationSchema = z.object({
    actualCostExGst: z.number().optional(),
    sourceType: z.enum(['manual', 'order', 'invoice', 'variation']).optional(),
    sourceId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).optional(),
    recordedAt: z.string().or(z.date()).optional(),
});
export const convertAllowanceSchema = z.object({
    finalAmountExGst: z.number(),
    createVariation: z.boolean().optional().default(false),
    variationDescription: z.string().max(500).optional(),
});
// ============================================================
// TIMESHEETS — Phase 1
// ============================================================
const uuidOptional = z.string().uuid().nullable().optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const positiveHours = z.number().positive().max(24);
export const createEmployeeSchema = z.object({
    code: z.string().max(50).optional(),
    workerType: z.enum(['employee', 'subcontractor']).default('employee'),
    firstName: z.string().max(100).optional(),
    lastName: z.string().max(100).optional(),
    email: z.string().email().max(255).optional().or(z.literal('')),
    phone: z.string().max(50).optional(),
    supplierId: uuidOptional,
    userId: uuidOptional,
    defaultCostRate: z.number().min(0).optional(),
    defaultChargeRate: z.number().min(0).optional(),
    /** Plain PIN — server hashes. 4-6 digits. */
    pin: z.string().regex(/^\d{4,6}$/).optional(),
}).refine(d => d.firstName || d.lastName || d.code, {
    message: 'At least one of firstName, lastName, or code is required',
});
export const updateEmployeeSchema = z.object({
    code: z.string().max(50).nullable().optional(),
    workerType: z.enum(['employee', 'subcontractor']).optional(),
    firstName: z.string().max(100).nullable().optional(),
    lastName: z.string().max(100).nullable().optional(),
    email: z.string().email().max(255).nullable().optional().or(z.literal('')),
    phone: z.string().max(50).nullable().optional(),
    supplierId: uuidOptional,
    userId: uuidOptional,
    defaultCostRate: z.number().min(0).optional(),
    defaultChargeRate: z.number().min(0).optional(),
    /** null clears the PIN. */
    pin: z.string().regex(/^\d{4,6}$/).nullable().optional(),
    isArchived: z.boolean().optional(),
});
export const createTimesheetEntrySchema = z.object({
    employeeId: z.string().uuid(),
    jobId: z.string().uuid(),
    costCentreId: uuidOptional,
    workDate: isoDate,
    hours: positiveHours,
    rateType: z.enum(['base', 'ot15', 'ot2']).default('base'),
    costRate: z.number().min(0).nullable().optional(),
    chargeRate: z.number().min(0).nullable().optional(),
    notes: z.string().max(1000).optional(),
    allocatedToAllowance: uuidOptional,
    clockInAt: z.string().datetime().nullable().optional(),
    clockOutAt: z.string().datetime().nullable().optional(),
    clockInLat: z.number().min(-90).max(90).nullable().optional(),
    clockInLng: z.number().min(-180).max(180).nullable().optional(),
    clockInAccuracyM: z.number().min(0).nullable().optional(),
});
export const updateTimesheetEntrySchema = z.object({
    costCentreId: uuidOptional,
    workDate: isoDate.optional(),
    hours: positiveHours.optional(),
    rateType: z.enum(['base', 'ot15', 'ot2']).optional(),
    costRate: z.number().min(0).nullable().optional(),
    chargeRate: z.number().min(0).nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
    allocatedToAllowance: uuidOptional,
    clockInAt: z.string().datetime().nullable().optional(),
    clockOutAt: z.string().datetime().nullable().optional(),
});
export const submitTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
});
export const approveTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
});
export const rejectTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
    reason: z.string().max(1000).optional(),
});
// ============================================================================
// Timesheet Me — mobile self-entry (PIN auth)
// ============================================================================
/** Login: tenant slug from URL param, employee code + PIN from body. */
export const timesheetMeLoginSchema = z.object({
    employeeCode: z.string().trim().min(1, 'Employee code is required').max(50),
    pin: z.string().regex(/^\d{4,6}$/, 'PIN must be 4–6 digits'),
});
/** Self-create — employee never sets their own employeeId, never sets rates. */
export const timesheetMeCreateEntrySchema = z.object({
    jobId: z.string().uuid(),
    costCentreId: uuidOptional,
    workDate: isoDate,
    hours: positiveHours,
    rateType: z.enum(['base', 'ot15', 'ot2']).default('base'),
    notes: z.string().max(1000).optional(),
});
/** Self-update — only own drafts; same field set as create. */
export const timesheetMeUpdateEntrySchema = z.object({
    jobId: z.string().uuid().optional(),
    costCentreId: uuidOptional,
    workDate: isoDate.optional(),
    hours: positiveHours.optional(),
    rateType: z.enum(['base', 'ot15', 'ot2']).optional(),
    notes: z.string().max(1000).nullable().optional(),
});
// ============================================================================
// Timesheet Settings (admin-configurable, stored in company_settings.metadata)
// ============================================================================
export const updateTimesheetSettingsSchema = z.object({
    timesheetSelfEntryLockoutDays: z.number().int().min(1).max(365),
});
// ============================================================================
// Client Portal Content (builder-curated, stored in company_settings.metadata)
// ============================================================================
// Only http(s) URLs — blocks javascript:/data: schemes from reaching an href.
const httpUrl = z
    .string()
    .trim()
    .max(500)
    .url()
    .refine((u) => /^https?:\/\//i.test(u), { message: 'Link must start with http:// or https://' });
export const portalContentSchema = z.object({
    portalContacts: z
        .array(z.object({
        name: z.string().trim().min(1).max(120),
        role: z.string().trim().max(120).optional(),
        email: z.string().trim().max(200).email().optional().or(z.literal('')),
        phone: z.string().trim().max(40).optional(),
    }))
        .max(20)
        .optional(),
    portalUsefulLinks: z
        .array(z.object({
        label: z.string().trim().min(1).max(120),
        url: httpUrl,
        description: z.string().trim().max(300).optional(),
    }))
        .max(50)
        .optional(),
    portalFaqs: z
        .array(z.object({
        question: z.string().trim().min(1).max(300),
        answer: z.string().trim().min(1).max(3000),
    }))
        .max(100)
        .optional(),
    // Free-form rich FAQ page (HTML). Sanitized client-side before save AND on
    // render; generous cap for an authored page with formatting/images.
    portalFaqHtml: z.string().max(50000).optional(),
    portalTradingName: z.string().trim().max(120).optional(),
    portalDigest: z.object({ enabled: z.boolean() }).optional(),
    portalStageNames: z.record(z.string().regex(/^\d{1,3}$/), z.string().trim().max(80)).optional(),
    portalDefaults: z
        .object({
        sharePassedInspections: z.boolean().optional(),
        shareDocumentTypeCodes: z.array(z.string().trim().max(20)).max(20).optional(),
        sharePhotosFromSubmittedLogs: z.boolean().optional(),
    })
        .optional(),
});
// ============================================================================
// Prestart Selections (a selection = a PC/PS allowance the client resolves)
// ============================================================================
export const selectionEnableSchema = z.object({
    required: z.boolean().optional(),
    // ISO date string (YYYY-MM-DD or full ISO); null clears the deadline.
    deadline: z.string().trim().max(40).optional().nullable(),
    // 'flat' = each option is a flat total (default); 'qty_rate' = option priced per-unit × the
    // line's measured BOQ quantity (e.g. bricks).
    pricingMode: z.enum(['flat', 'qty_rate']).optional(),
});
// A per-unit/flat add-on cost on an option (e.g. extra laying/cleaning labour).
export const selectionAddOnSchema = z.object({
    label: z.string().trim().min(1).max(120),
    rate: z.number().min(0).max(99999999).optional(), // per-unit, × line quantity
    flat: z.number().min(0).max(99999999).optional(), // one-off
});
export const selectionOptionSchema = z.object({
    name: z.string().trim().min(1).max(500),
    // Rich HTML (sanitised on render) — markup needs more room than plain text.
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    priceExGst: z.number().min(0).max(99999999), // flat-mode total
    unitRate: z.number().min(0).max(99999999).optional(), // qty_rate-mode per-unit cost
    imageUrl: httpUrl.optional().or(z.literal('')),
    catalogueItemId: z.string().uuid().optional().nullable(),
    isDefault: z.boolean().optional(),
    isStandardInclusion: z.boolean().optional(),
    addOns: z.array(selectionAddOnSchema).max(10).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const selectionOptionUpdateSchema = selectionOptionSchema.partial();
// Homeowner picking an option (portal write).
export const selectionChoiceSchema = z.object({
    optionId: z.string().uuid(),
    note: z.string().trim().max(1000).optional().or(z.literal('')),
});
// Staff setting/clearing the client's choice on their behalf (builder write).
// optionId null = clear the current selection.
export const selectionStaffChoiceSchema = z.object({
    optionId: z.string().uuid().nullable(),
});
// ── Selection TEMPLATES (reusable library) + SECTIONS (headings) ─────────────
// Client-generated ids for in-document nodes (uuid or nanoid).
const nodeId = z.string().trim().min(1).max(64);
// A template/section ITEM is either a priced client Selection (with options) or a
// standard Inclusion (heading/text line, no choice).
export const templateItemSchema = z.object({
    id: nodeId,
    type: z.enum(['selection', 'inclusion']),
    name: z.string().trim().min(1).max(500),
    // Rich HTML (sanitised on render): the inclusion text, or selection notes.
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    // selection-only:
    pricingMode: z.enum(['flat', 'qty_rate']).optional(),
    required: z.boolean().optional(),
    defaultQuantity: z.number().min(0).max(99999999).optional(),
    estimateExGst: z.number().min(0).max(99999999).optional(), // the allowance estimate (line unit_price)
    costCentreCode: z.string().trim().max(20).optional().or(z.literal('')),
    options: z.array(selectionOptionSchema).max(80).optional(),
});
export const templateSectionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(200),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    items: z.array(templateItemSchema).max(300),
});
export const selectionTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional().or(z.literal('')),
    sections: z.array(templateSectionSchema).max(80),
});
// Duplicate a template (optional new name).
export const templateDuplicateSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
});
// Apply a template's structure onto a job.
export const applyTemplateSchema = z.object({
    jobId: z.string().uuid(),
});
// Job-side spec document = ordered sections, each with a heading + inclusion rows.
// (Priced selections are bill_items tagged with metadata.selection.sectionId.)
export const specInclusionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(500),
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const specSectionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(200),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    inclusions: z.array(specInclusionSchema).max(300).optional(),
});
export const jobSpecSchema = z.object({
    sections: z.array(specSectionSchema).max(80),
});
// Assign a selection (bill_item) to a section (null = ungrouped).
export const selectionSectionSchema = z.object({
    sectionId: nodeId.nullable(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
// ============================================================================
// Platform Admin — Usage Insights
// ============================================================================
export const adminTenantListQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
});
export const adminTenantIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// ============================================================================
// Platform Admin — Customer lead cleanup & salespeople management
// ============================================================================
// Bulk soft-delete a set of leads (is_deleted = true; recoverable).
export const bulkDeleteLeadsSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(200),
});
// Clean up leads by status (default: unverified sign-ups). Soft-deletes matching
// leads; `onlyUnprovisioned` protects any lead already attached to a real tenant,
// and `preview` returns the count that WOULD be deleted without deleting.
export const cleanupLeadsSchema = z.object({
    status: z.string().trim().min(1).max(50).default('pending_verification'),
    olderThanDays: z.number().int().min(0).max(3650).optional(),
    onlyUnprovisioned: z.boolean().default(true),
    preview: z.boolean().default(false),
});
// Toggle whether a platform user appears in the lead-assignment salesperson list.
export const setSalespersonSchema = z.object({
    isSalesperson: z.boolean(),
});
export const adminUserIdParamsSchema = z.object({
    userId: z.string().uuid(),
});
// Free/consumer email domains — a corporate lead usually has its own domain, so a
// free-mail address is a (soft) signal to eyeball a lead's genuineness. NOT a
// block: plenty of real sole-trader builders use gmail/bigpond.
export const FREE_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.com.au', 'ymail.com',
    'hotmail.com', 'hotmail.com.au', 'outlook.com', 'outlook.com.au', 'live.com',
    'live.com.au', 'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
    'protonmail.com', 'proton.me', 'gmx.com', 'mail.com', 'yandex.com',
    'bigpond.com', 'bigpond.net.au', 'optusnet.com.au', 'iinet.net.au',
    'tpg.com.au', 'internode.on.net', 'westnet.com.au',
];
export function isFreeEmailDomain(domain) {
    if (!domain)
        return false;
    return FREE_EMAIL_DOMAINS.includes(domain.trim().toLowerCase());
}
// ============================================================================
// Platform Admin — API Keys (MCP / programmatic access)
// ============================================================================
export const adminCreateApiKeySchema = z.object({
    tenantId: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
    isAdmin: z.boolean().default(false),
});
export const adminApiKeyIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// Self-service API keys (public API governance — /api/v1/keys). The tenant comes
// from the signed-in user (never the body) and is_admin is always false, so this
// schema only carries the name + global scope. Per-domain scopes layer on later.
export const createApiKeySchema = z.object({
    // `oauth:` is reserved for connector-minted keys (they expire + auto-revoke).
    name: z.string().trim().min(1).max(100)
        .refine(n => !n.toLowerCase().startsWith('oauth:'), { message: 'Names starting with "oauth:" are reserved for connector keys' }),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
});
export const apiKeyIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// Per-domain API-key scopes (Phase C.2). Domains map 1:1 to the v1 resource
// routers. A key with NO scopes set has full access at its global read/write
// level (backwards-compatible); once any scope is set, it's an ALLOWLIST.
export const API_KEY_DOMAINS = ['jobs', 'catalogue', 'suppliers', 'cost-centres', 'contacts', 'units', 'orders', 'boq', 'selections', 'usage'];
export const apiKeyScopeActionSchema = z.enum(['none', 'read', 'read_write']);
export const setApiKeyScopesSchema = z.object({
    scopes: z.record(z.enum(API_KEY_DOMAINS), apiKeyScopeActionSchema),
});
// Outbound webhooks (Phase E). MVP "safe to expose" events — financial internals
// (invoice.paid, approval.*, payment_batch.*, transaction.*, claim.*) are never
// exposed.
export const WEBHOOK_EVENTS = [
    'job.created',
    'job.updated',
    'order.created',
    'order.status_changed',
    'catalogue_item.created',
    'catalogue_item.price_updated',
    'rfq.created',
    'rfq.response_received',
];
export const createWebhookSchema = z.object({
    url: z.string().url().max(2048),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1),
    description: z.string().trim().max(200).optional(),
});
export const updateWebhookSchema = z.object({
    url: z.string().url().max(2048).optional(),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1).optional(),
    description: z.string().trim().max(200).optional(),
    active: z.boolean().optional(),
});
export const webhookIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// ============================================================================
// MCP OAuth (Tier 2B) — claude.ai / connector authorization flow
// ============================================================================
/** RFC 7591 dynamic client registration. Unknown metadata is accepted and
 *  ignored (passthrough) per spec. */
export const oauthRegisterClientSchema = z.object({
    redirect_uris: z.array(z.string().url().max(500)).min(1).max(10),
    client_name: z.string().trim().max(200).optional(),
}).passthrough();
/** Consent approval posted by the SPA consent page (JWT-authenticated). */
export const oauthApproveSchema = z.object({
    client_id: z.string().min(1).max(100),
    redirect_uri: z.string().url().max(500),
    state: z.string().max(1000).optional(),
    code_challenge: z.string().min(43).max(128),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
});
/** Consent denial posted by the SPA consent page. Only needs the client +
 *  registered redirect so the server can send the user back to the client with
 *  error=access_denied instead of stranding them on costed.app. */
export const oauthDenySchema = z.object({
    client_id: z.string().min(1).max(100),
    redirect_uri: z.string().url().max(500),
    state: z.string().max(1000).optional(),
});
/** RFC 6749 token endpoint (public client). Two grants:
 *  - authorization_code + PKCE (first connect)
 *  - refresh_token (silent renewal; the refresh token is rotated on every use) */
export const oauthTokenSchema = z.discriminatedUnion('grant_type', [
    z.object({
        grant_type: z.literal('authorization_code'),
        code: z.string().min(1).max(200),
        redirect_uri: z.string().url().max(500),
        client_id: z.string().min(1).max(100),
        code_verifier: z.string().min(43).max(128),
    }),
    z.object({
        grant_type: z.literal('refresh_token'),
        refresh_token: z.string().min(1).max(200),
        client_id: z.string().min(1).max(100),
        scope: z.enum(['read_only', 'read_write']).optional(),
    }),
]);
/** RFC 7009 token revocation — accepts either the access token (costed_sk_…)
 *  or the refresh token. Always answers 200 per spec. */
export const oauthRevokeSchema = z.object({
    token: z.string().min(1).max(200),
    client_id: z.string().min(1).max(100),
    token_type_hint: z.enum(['access_token', 'refresh_token']).optional(),
});
// ============================================================================
// In-app assistant ("Ask Costed")
// ============================================================================
const assistantMessageSchema = z.object({
    // user/assistant for chat turns; tool/system appear in the AI SDK ModelMessage
    // history the client echoes back (e.g. tool-result turns after a confirm).
    role: z.enum(['user', 'assistant', 'tool', 'system']),
    // string for plain turns; array of content parts for tool-call/tool-result.
    // A single assistant turn can hold several tool-call parts, so keep headroom.
    content: z.union([z.string().max(20000), z.array(z.any()).max(100)]),
});
export const assistantChatSchema = z.object({
    // Agentic sessions accumulate fast (each tool call adds a call + result
    // message), so this ceiling has to be generous — a multi-step recipe/RFQ
    // build easily passes 50 turns. The natural bound is the model context
    // window / BYOK cost, not this guard. (Client trimming is a future option.)
    messages: z.array(assistantMessageSchema).min(1).max(250),
    approvals: z.record(z.enum(['approve', 'reject'])).optional(),
    approvalTokens: z.record(z.string().max(200)).optional(),
    // Which saved assistant model to use (id from assistantModels); omit for default.
    modelId: z.string().max(100).optional(),
    // Where the user currently is in the app (route path incl. query), so the
    // assistant can ground "this job" / "this page".
    context: z.object({
        path: z.string().max(300),
    }).optional(),
});
// ----------------------------------------------------------------------------
// Ask Costed conversation history (server-side persistence)
// ----------------------------------------------------------------------------
// One visible transcript bubble (what the user sees).
const conversationDisplayMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    text: z.string().max(20000),
    // Image attachments on user turns (data URLs, client-downscaled ~1280px
    // JPEG). Bounded hard: 3 per message, ~650KB binary each.
    images: z.array(z.string().startsWith('data:image/').max(900_000)).max(3).optional(),
});
// Save (upsert) a conversation. `id` present → update that conversation;
// absent → create a new one. `wire` is the opaque model history (reuses the
// chat message schema, so it tolerates tool/system turns); omitted while a
// confirmation is pending so a dangling tool call is never persisted.
export const saveConversationSchema = z.object({
    id: z.string().uuid().optional(),
    title: z.string().max(200).optional(),
    messages: z.array(conversationDisplayMessageSchema).min(1).max(500),
    wire: z.array(assistantMessageSchema).max(500).optional(),
    modelId: z.string().max(100).optional(),
});
// ============================================================================
// Assistant provider config (bring-your-own-model: Claude/ChatGPT/Gemini/DeepSeek)
// ============================================================================
export const assistantProviderConfigSchema = z.object({
    provider: z.enum(['anthropic', 'openai', 'google', 'deepseek']),
    model: z.string().trim().min(1).max(100),
    apiKey: z.string().trim().min(10).max(400),
});
export const assistantModelAddSchema = assistantProviderConfigSchema.extend({
    label: z.string().trim().max(60).optional(),
});
// ============================================================================
// AI activity (provenance log of AI-performed writes — assistant + MCP)
// ============================================================================
/** Logged by the tool registry after a successful AI write. */
export const aiActivityLogSchema = z.object({
    tool: z.string().trim().min(1).max(80),
    summary: z.string().trim().min(1).max(500),
    refs: z
        .object({
        type: z.enum(['catalogue_item', 'rfq', 'boq_load', 'report_definition', 'query_block', 'report_writer_template', 'lead', 'quote', 'portal', 'document', 'transmittal', 'folder']),
    })
        .catchall(z.union([z.string().max(120), z.number()]))
        .refine((r) => Object.keys(r).length <= 12 && Object.keys(r).every((k) => k.length <= 40), 'refs must have at most 12 keys with names up to 40 chars')
        .optional(),
});
export const aiActivityReviewSchema = z.object({
    action: z.enum(['reviewed', 'dismissed']),
});
/** Query params for GET /api/help/search (assistant's costed_search_help). */
export const helpSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(200),
    limit: z.coerce.number().int().min(1).max(10).default(5),
});
// ============================================================================
// RFQ quote submission (supplier public response + estimator record-quote)
// ============================================================================
const rfqQuotedItemSchema = z.object({
    rfqItemId: z.string().min(1).max(60),
    quotedPrice: z.number().min(0).max(1_000_000_000),
    supplierReference: z.string().max(120).nullish(),
    notes: z.string().max(2000).nullish(),
    supersededBy: z.string().max(300).nullish(),
    supplierUnit: z.string().max(40).nullish(),
    supplierPackQty: z.number().positive().max(1_000_000).nullish(),
});
/** Body for POST /rfq-response/:slug/:token AND /rfqs/:id/suppliers/:sid/quote. */
// ---- RFQ + supplier bodies (module pass 2). Every write on the two routers is validated. ----
const rfqDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const uuidOrEmpty = z.union([z.string().uuid(), z.literal(''), z.null()]).optional().transform((v) => (v ? v : undefined));
export const rfqCreateSchema = z.object({
    jobId: uuidOrEmpty,
    costCentreId: uuidOrEmpty,
    bLoad: z.number().int().min(0).max(9999).optional(),
    description: z.string().trim().max(300).optional(),
    validUntil: z.union([rfqDay, z.literal(''), z.null()]).optional().transform((v) => (v ? v : undefined)),
    notes: z.string().max(4000).nullish(),
    coverLetter: z.string().max(8000).nullish(),
    metadata: z.record(z.unknown()).optional(),
});
export const rfqUpdateSchema = z.object({
    description: z.string().trim().max(300).optional(),
    validUntil: z.union([rfqDay, z.literal(''), z.null()]).optional().transform((v) => (v === undefined ? undefined : v || null)),
    notes: z.string().max(4000).nullish(),
    coverLetter: z.string().max(8000).nullish(),
    costCentreId: uuidOrEmpty,
    bLoad: z.number().int().min(0).max(9999).optional(),
});
export const rfqItemSchema = z.object({
    catalogueItemId: uuidOrEmpty,
    priceCode: z.string().trim().max(100).nullish(),
    description: z.string().trim().min(1, 'Each item needs a description').max(500),
    quantity: z.number().min(0).max(1_000_000_000).optional(),
    unitId: uuidOrEmpty,
    unitDisplay: z.string().trim().max(50).nullish(),
    currentPrice: z.number().min(0).max(1_000_000_000).optional(),
    notes: z.string().max(2000).nullish(),
});
/** One item or a list of them — the route accepts both shapes. */
export const rfqItemsSchema = z.union([z.array(rfqItemSchema).min(1).max(500), rfqItemSchema.transform((i) => [i])]);
export const rfqFromBoqSchema = z.object({ jobId: z.string().uuid(), costCentreId: uuidOrEmpty, bLoad: z.number().int().min(0).max(9999).optional() });
export const rfqFromCatalogueSchema = z.object({ catalogueItemIds: z.array(z.string().uuid()).min(1).max(500) });
export const rfqSuppliersSchema = z.object({ supplierIds: z.array(z.string().uuid()).min(1).max(100) });
export const rfqReasonSchema = z.object({ reason: z.string().trim().max(1000).optional() });
export const supplierBankingSchema = z.object({
    bsb: z.string().trim().max(10).nullish(),
    accountNumber: z.string().trim().max(20).nullish(),
    accountName: z.string().trim().max(100).nullish(),
    bankName: z.string().trim().max(100).nullish(),
    paymentReference: z.string().trim().max(50).nullish(),
    remittanceType: z.string().trim().max(20).nullish(),
    remittanceEmail: z.union([z.string().trim().email().max(255), z.literal('')]).nullish(),
}).passthrough();
export const supplierCreateSchema = z.object({
    code: z.string().trim().min(1, 'Supplier code is required').max(20),
    name: z.string().trim().min(1, 'Supplier name is required').max(200),
    email: z.union([z.string().trim().email('Supplier email is not a valid address').max(255), z.literal('')]).nullish(),
    abn: z.string().trim().max(20).nullish(),
    paymentTerms: z.number().int().min(0).max(365).optional(),
    isGstRegistered: z.boolean().optional(),
    gstFreeByDefault: z.boolean().optional(),
    groupId: uuidOrEmpty,
    banking: supplierBankingSchema.optional(),
}).passthrough();
export const supplierUpdateSchema = supplierCreateSchema.partial();
export const supplierPriceSchema = z.object({
    priceCode: z.string().trim().max(100).optional(),
    catalogueItemId: uuidOrEmpty,
    price: z.number().gt(0, 'Price must be greater than zero').max(1_000_000_000),
    supplierReference: z.string().trim().max(100).nullish(),
    effectiveDate: z.union([rfqDay, z.literal('')]).nullish(),
    expiryDate: z.union([rfqDay, z.literal('')]).nullish(),
    priceLevel: z.number().int().min(1).max(5).optional(),
    area: z.string().trim().max(50).nullish(),
    comments: z.string().trim().max(500).nullish(),
}).passthrough();
export const supplierPriceUpdateSchema = supplierPriceSchema.partial();
// ---- Purchase orders (module pass 3) ----
const orderDay = z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'), z.literal(''), z.null()]).optional().transform((v) => (v ? v : undefined));
export const orderCreateSchema = z.object({
    jobId: z.string().uuid().optional(),
    jobNo: z.string().trim().min(1).max(20).optional(),
    costCentreId: z.string().uuid({ message: 'A cost centre is required' }),
    bLoad: z.number().int().min(1).max(999).optional().default(1),
    supplierId: z.union([z.string().uuid(), z.literal(''), z.null()]).optional().transform((v) => (v ? v : undefined)),
    orderDate: orderDay,
    deliveryDate: orderDay,
    notes: z.string().max(4000).nullish(),
}).refine((d) => d.jobId || d.jobNo, { message: 'A job is required', path: ['jobNo'] });
export const orderUpdateSchema = z.object({
    supplierId: z.union([z.string().uuid(), z.literal(''), z.null()]).optional().transform((v) => (v ? v : undefined)),
    costCentreId: z.string().uuid().optional(),
    orderDate: orderDay,
    deliveryDate: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal(''), z.null()]).optional().transform((v) => (v === undefined ? undefined : v || null)),
    notes: z.string().max(4000).nullish(),
}).strict();
export const orderEmailSettingsSchema = z.object({
    email: z.boolean().optional(),
    to: z.union([z.string().trim().email('Recipient must be a valid email address'), z.literal('')]).optional(),
    cc: z.union([z.string().trim().email('CC must be a valid email address'), z.literal('')]).optional(),
    subject: z.string().trim().max(200).optional(),
    message: z.string().max(4000).optional(),
    transmittalId: z.union([z.string().uuid(), z.literal('')]).optional(),
});
export const orderSendSchema = z.object({ emailSettings: orderEmailSettingsSchema.optional() });
export const orderCancelSchema = z.object({ reason: z.string().trim().min(3, 'Give a reason for cancelling (at least 3 characters)').max(500) });
export const orderFullyInvoicedSchema = z.object({ isFullyInvoiced: z.boolean().optional() });
export const orderMetadataSchema = z.object({ metadata: z.record(z.unknown()).refine((m) => Object.keys(m).length > 0 && Object.keys(m).length <= 50, 'metadata must have 1–50 keys') });
export const orderImportSchema = z.object({
    orders: z.array(z.record(z.unknown())).min(1).max(5000),
    skipExisting: z.boolean().optional(),
    dryRun: z.boolean().optional(),
});
export const orderPdfQuerySchema = z.object({ template: z.string().max(50).optional() });
// ── BOQ & estimating (module review #4) ─────────────────────────────────────
const boqNo = z.string().trim().min(1).max(20);
const boqCode = z.string().trim().min(1).max(50);
export const boqLoadSchema = z.coerce.number().int().min(0).max(999);
const boqQty = z.coerce.number().finite();
export const boqItemAddSchema = z.object({
    jobNo: boqNo.optional(), jobId: z.string().uuid().optional(),
    costCentre: boqNo.optional(), costCentreId: z.string().uuid().optional(),
    priceCode: boqCode.optional(), catalogueItemId: z.string().uuid().optional(),
    quantity: boqQty.optional(), quantityFormula: z.string().max(500).nullable().optional(),
    unitPrice: boqQty.optional(), workup: z.string().max(4000).nullable().optional(),
    supplierId: z.string().uuid().nullable().optional(), bLoad: boqLoadSchema.optional(),
})
    .refine((d) => d.jobNo || d.jobId, 'jobNo or jobId is required')
    .refine((d) => d.costCentre || d.costCentreId, 'costCentre or costCentreId is required')
    .refine((d) => d.priceCode || d.catalogueItemId, 'priceCode is required');
export const boqItemUpdateSchema = z.object({
    quantity: boqQty.optional(), quantityFormula: z.string().max(500).nullable().optional(),
    unitPrice: boqQty.optional(), workup: z.string().max(4000).nullable().optional(),
    supplierId: z.string().uuid().nullable().optional(), costCentreId: z.string().uuid().optional(), bLoad: boqLoadSchema.optional(),
}).refine((d) => Object.values(d).some((v) => v !== undefined), 'No fields to update');
export const boqAdhocItemSchema = z.object({
    jobNo: boqNo, costCentre: boqNo, description: z.string().trim().min(1).max(500), quantity: boqQty,
    unitCode: boqNo, unitPrice: boqQty, bLoad: boqLoadSchema.default(1), workup: z.string().max(4000).nullable().optional(),
});
export const boqHeadingSchema = z.object({ jobNo: boqNo, costCentre: boqNo, bLoad: boqLoadSchema.optional(), subHeading: z.boolean().optional(), text: z.string().max(500).optional() });
export const boqChangeCodeSchema = z.object({ priceCode: boqCode, keepPrice: z.boolean().optional() });
export const boqUnitSchema = z.object({ unitId: z.string().uuid().nullable().optional() });
export const boqIdsSchema = z.object({ ids: z.array(z.string().min(1).max(64)).min(1).max(500) });
export const boqReorderSchema = z.object({ jobNo: boqNo, items: z.array(z.object({ id: z.string().min(1).max(64), lineNumber: z.coerce.number().int().min(1) })).min(1).max(2000) });
export const boqMoveSchema = z.object({ jobNo: boqNo, itemIds: z.array(z.string().uuid()).min(1).max(2000), targetCostCentreCode: boqNo });
export const boqRepriceBillSchema = z.object({ costCentre: boqNo.optional(), bLoad: boqLoadSchema.optional(), priceLevel: z.coerce.number().int().min(1).max(10).optional(), scope: z.string().max(20).optional() });
export const boqRepriceItemsSchema = z.object({ itemIds: z.array(z.string().uuid()).min(1).max(2000), priceLevel: z.coerce.number().int().min(1).max(10).optional() });
export const boqExplodeSchema = z.object({
    quantity: boqQty, variableValues: z.record(z.coerce.number()).optional(),
    options: z.object({ removeParent: z.boolean().optional(), explodeRecipes: z.boolean().optional() }).optional(),
    billItemId: z.string().max(64).optional(),
    priceLevel: z.coerce.number().int().min(1).max(5).optional(),
});
export const boqCreateLoadSchema = z.object({ costCentre: boqNo, supplierId: z.string().uuid().nullable().optional(), copyFrom: boqLoadSchema.optional() });
// ── Catalogue & pricing (module review #5) ──────────────────────────────────
const catCode = z.string().trim().min(1).max(50);
const catShort = z.string().trim().max(20);
export const catalogueLevelSchema = z.coerce.number().int().min(1, 'Price level is 1–5').max(5, 'Price level is 1–5');
const catDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-06');
const catMoney = z.coerce.number().finite().min(0, 'Price cannot be negative');
const someField = (d) => Object.values(d).some((v) => v !== undefined);
export const catalogueItemCreateSchema = z.object({
    priceCode: catCode, description: z.string().trim().min(1, 'Description is required').max(500),
    unitCode: catShort.optional(), unitId: z.string().uuid().optional(),
    costCentreCode: catShort.optional(), costCentreId: z.string().uuid().optional(),
    isRecipe: z.boolean().optional(), isRecipeIngredient: z.boolean().optional(),
    template: z.string().max(4000).nullable().optional(), specification: z.string().max(4000).nullable().optional(),
    currentPrice: catMoney.optional(), costType: z.string().max(50).nullable().optional(), takeoffType: z.string().max(50).nullable().optional(),
});
export const catalogueItemUpdateSchema = z.object({
    description: z.string().trim().min(1).max(500).optional(),
    unitCode: catShort.nullable().optional(), unitId: z.string().uuid().nullable().optional(),
    costCentreCode: catShort.nullable().optional(), costCentreId: z.string().uuid().nullable().optional(),
    isRecipe: z.boolean().optional(), isRecipeIngredient: z.boolean().optional(), isArchived: z.boolean().optional(),
    template: z.string().max(4000).nullable().optional(), specification: z.string().max(4000).nullable().optional(),
    costType: z.string().max(50).nullable().optional(), takeoffType: z.string().max(50).nullable().optional(),
}).refine(someField, 'No fields to update');
export const cataloguePriceAddSchema = z.object({
    price: catMoney, priceLevel: catalogueLevelSchema.default(1), effectiveDate: catDay.optional(),
    isManualOverride: z.boolean().optional(), changeReason: z.string().max(500).nullable().optional(),
});
export const catalogueSupplierPriceSchema = z.object({
    supplierCode: catCode, price: catMoney, supplierReference: z.string().max(100).nullable().optional(),
    effectiveDate: catDay.nullable().optional(), expiryDate: catDay.nullable().optional(), priceLevel: catalogueLevelSchema.optional(),
    area: z.string().max(100).nullable().optional(), comments: z.string().max(1000).nullable().optional(),
});
export const catalogueSupplierPriceUpdateSchema = catalogueSupplierPriceSchema.partial().extend({ supplierCode: catCode });
export const catalogueSupplierPriceDeleteSchema = z.object({ supplierCode: catCode });
export const catalogueRecipeSchema = z.object({
    components: z.array(z.object({
        priceCode: catCode, quantity: z.coerce.number().finite().optional(), formula: z.string().max(500).nullable().optional(),
        notes: z.string().max(1000).nullable().optional(), sortOrder: z.coerce.number().int().optional(), costCentreOverride: z.string().max(64).nullable().optional(),
    })).max(500),
});
export const cataloguePriceCodesSchema = z.object({ priceCodes: z.array(catCode).min(1, 'Select at least one item').max(5000) });
export const catalogueBulkArchiveSchema = cataloguePriceCodesSchema.extend({ archive: z.boolean().optional() });
export const catalogueBulkCostCentreSchema = cataloguePriceCodesSchema.extend({ costCentreCode: catShort.min(1) });
export const catalogueBulkUnitSchema = cataloguePriceCodesSchema.extend({ unitCode: catShort.min(1) });
export const catalogueBulkTypesSchema = cataloguePriceCodesSchema.extend({ costType: z.string().max(50).nullable().optional(), takeoffType: z.string().max(50).nullable().optional() })
    .refine((d) => d.costType !== undefined || d.takeoffType !== undefined, 'At least one of costType or takeoffType is required');
export const catalogueBulkFindReplaceSchema = cataloguePriceCodesSchema.extend({ findText: z.string().min(1).max(500), replaceText: z.string().max(500).optional() });
export const catalogueBulkAppendSchema = cataloguePriceCodesSchema.extend({ appendText: z.string().min(1).max(500) });
export const catalogueBulkRepriceSchema = cataloguePriceCodesSchema.extend({ priceLevel: catalogueLevelSchema, changeType: z.enum(['percent', 'fixed', 'set']), changeValue: z.coerce.number().finite() });
export const catalogueBulkCopyLevelSchema = cataloguePriceCodesSchema.extend({ sourceLevel: catalogueLevelSchema, targetLevel: catalogueLevelSchema })
    .refine((d) => d.sourceLevel !== d.targetLevel, 'Source and target level must differ');
export const catalogueBulkPriceChangesSchema = z.object({
    priceType: z.enum(['estimate', 'supplier']),
    changes: z.array(z.object({ priceCode: catCode, level: catalogueLevelSchema.nullable().optional(), newPrice: catMoney, priceId: z.string().uuid().optional() })).min(1).max(5000),
    validFrom: catDay.optional(), estimator: z.string().max(200).optional(), notes: z.string().max(500).nullable().optional(),
});
export const unitCreateSchema = z.object({
    code: z.string().trim().min(1, 'Unit code is required').max(20), printout: z.string().trim().min(1, 'Unit printout is required').max(50),
    display: z.string().max(50).nullable().optional(), divisor: z.coerce.number().finite().positive('Divisor must be greater than 0').optional(),
    calculationRoutine: z.coerce.number().int().min(0).max(17).optional(), rounding: z.coerce.number().finite().min(0).optional(),
}).passthrough();
export const unitUpdateSchema = unitCreateSchema.partial().passthrough();
export const costCentreCreateSchema = z.object({
    code: z.string().trim().min(1, 'Cost centre code is required').max(20), name: z.string().trim().min(1, 'Cost centre name is required').max(200),
    tier: z.coerce.number().int().min(1).max(5).optional(), parentCode: catShort.nullable().optional(), subGroup: z.string().max(100).nullable().optional(),
    sortOrder: z.coerce.number().int().optional(), markup: z.coerce.number().finite().optional(), glAccount: z.string().max(50).nullable().optional(),
}).passthrough();
export const costCentreUpdateSchema = costCentreCreateSchema.partial().passthrough();
export const costCentreDeleteAllSchema = z.object({ confirm: z.literal('DELETE ALL COST CENTRES', { errorMap: () => ({ message: 'Type DELETE ALL COST CENTRES to confirm' }) }) });
export const costCentreIdsSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(1000) });
// ── Jobs, projects & selections (module review #6) ──────────────────────────
const jobDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-06');
const jobMoney = z.coerce.number().finite();
const uuidOrNull = z.string().uuid().nullable().optional();
const emptyToNull = (inner) => z.preprocess((v) => (v === '' ? null : v), inner);
/** Job statuses the list hides; setting or renaming to one needs owner/admin/manager. */
export const HIDDEN_JOB_STATUSES = ['template', 'templates', 'office use', 'office', 'internal', 'status name', 'archived'];
export const isHiddenJobStatus = (s) => !!s && HIDDEN_JOB_STATUSES.includes(s.trim().toLowerCase());
const jobFields = {
    name: z.string().trim().min(1, 'Job name is required').max(200),
    status: z.string().trim().min(1).max(50).optional(),
    clientId: emptyToNull(uuidOrNull), estimatorId: emptyToNull(uuidOrNull), supervisorId: emptyToNull(uuidOrNull),
    startDate: emptyToNull(jobDay.nullable().optional()), endDate: emptyToNull(jobDay.nullable().optional()),
    contractPrice: jobMoney.optional(), contractGst: jobMoney.optional(), budget: jobMoney.optional(),
    siteStreet: z.string().max(200).nullable().optional(), siteSuburb: z.string().max(100).nullable().optional(),
    siteState: z.string().max(50).nullable().optional(), sitePostcode: z.string().max(20).nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
};
const datesOrdered = (d) => !d.startDate || !d.endDate || d.endDate >= d.startDate;
export const jobCreateSchema = z.object({ jobNo: z.string().trim().min(1, 'Job number is required').max(20), ...jobFields })
    .refine(datesOrdered, 'End date cannot be before the start date');
export const jobUpdateSchema = z.object({ ...jobFields, name: jobFields.name.optional(), isArchived: z.boolean().optional() })
    .refine((d) => Object.values(d).some((v) => v !== undefined), 'No fields to update')
    .refine(datesOrdered, 'End date cannot be before the start date');
export const jobCopySchema = z.object({
    newJobNo: z.string().trim().min(1, 'New job number is required').max(20), name: z.string().trim().min(1).max(200).optional(),
    copyOptions: z.object({ copyBill: z.boolean().optional(), copyOrders: z.boolean().optional() }).optional(),
});
export const jobVariablesSchema = z.record(z.union([z.number().finite(), z.string().max(200), z.boolean(), z.null()]))
    .refine((v) => Object.keys(v).length <= 100, 'At most 100 variables');
export const jobStatusRenameSchema = z.object({ oldName: z.string().trim().min(1).max(50), newName: z.string().trim().min(1, 'The new status name cannot be empty').max(50) })
    .refine((d) => d.oldName !== d.newName, 'The names are the same');
export const projectStatusSchema = z.enum(['active', 'on_hold', 'completed', 'cancelled']);
const projectFields = {
    name: z.string().trim().min(1, 'Project name is required').max(200), description: z.string().max(4000).nullable().optional(),
    jobNo: z.string().trim().max(20).nullable().optional(), status: projectStatusSchema.optional(), color: z.string().max(20).optional(),
    priority: z.string().trim().max(20).optional(), startDate: emptyToNull(jobDay.nullable().optional()), dueDate: emptyToNull(jobDay.nullable().optional()),
    completedDate: emptyToNull(jobDay.nullable().optional()), ownerName: z.string().max(200).nullable().optional(),
    isTemplate: z.boolean().optional(), templateId: z.string().uuid().nullable().optional(),
};
const projDates = (d) => !d.startDate || !d.dueDate || d.dueDate >= d.startDate;
export const projectCreateSchema = z.object(projectFields).refine(projDates, 'Due date cannot be before the start date');
export const projectUpdateSchema = z.object({ ...projectFields, name: projectFields.name.optional() })
    .refine((d) => Object.values(d).some((v) => v !== undefined), 'No fields to update').refine(projDates, 'Due date cannot be before the start date');
export const taskStatusValueSchema = z.string().trim().min(1).max(50);
const taskFields = {
    title: z.string().trim().min(1, 'Task title is required').max(500), description: z.string().max(8000).nullable().optional(), notes: z.string().max(8000).nullable().optional(),
    status: taskStatusValueSchema.optional(), priority: z.string().trim().max(20).optional(), percentComplete: z.coerce.number().int().min(0).max(100).optional(),
    assigneeName: z.string().max(200).nullable().optional(), assignedUserId: emptyToNull(uuidOrNull),
    startDate: emptyToNull(jobDay.nullable().optional()), dueDate: emptyToNull(jobDay.nullable().optional()),
    estimatedHours: z.coerce.number().min(0).nullable().optional(), parentTaskId: emptyToNull(uuidOrNull),
};
export const taskCreateSchema = z.object(taskFields).passthrough().refine(projDates, 'Due date cannot be before the start date');
export const taskUpdateSchema = z.object({ ...taskFields, title: taskFields.title.optional() }).passthrough().refine(projDates, 'Due date cannot be before the start date');
export const taskStatusSchema = z.object({ status: taskStatusValueSchema, columnOrder: z.coerce.number().int().min(0).optional() });
const taskIds = z.array(z.string().uuid()).min(1, 'Select at least one task').max(1000);
export const taskBulkStatusSchema = z.object({ taskIds, status: taskStatusValueSchema });
export const taskBulkAssigneeSchema = z.object({ taskIds, assigneeName: z.string().max(200).nullable().optional(), assignedUserId: z.string().uuid().nullable().optional() });
export const taskBulkShiftSchema = z.object({ taskIds, shiftDays: z.coerce.number().int().min(-3650).max(3650) });
export const taskIdsSchema = z.object({ taskIds });
export const recycleBinEntityTypeSchema = z.enum(['job', 'contact', 'supplier', 'catalogue_item', 'document']);
/** Signed, time-limited download links for documents (POST /documents/:id/link and /documents/links). */
export const documentLinkSchema = z.object({
    expiresInHours: z.coerce.number().int().min(1).max(168).default(24),
    inline: z.boolean().optional(),
});
export const documentLinksBatchSchema = documentLinkSchema.extend({
    documentIds: z.array(z.string().uuid()).min(1).max(100),
});
// ── Site: diaries, inspections, defects, safety (module review #7) ───────────
const siteDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-06');
const siteUuid = z.string().uuid();
const siteUuidOrNull = z.preprocess((v) => (v === '' ? null : v), siteUuid.nullable().optional());
const siteText = (max) => z.string().max(max).nullable().optional();
const someOf = (d) => Object.values(d).some((v) => v !== undefined);
export const dailyLogStatusSchema = z.enum(['draft', 'published']);
const dailyLogFields = {
    weatherCondition: siteText(50), temperatureHigh: z.coerce.number().finite().nullable().optional(), temperatureLow: z.coerce.number().finite().nullable().optional(),
    weatherNotes: siteText(2000), workersOnSite: z.coerce.number().int().min(0).max(10000).optional(),
    workersDetails: z.array(z.object({ trade: z.string().max(100).optional(), company: z.string().max(200).optional(), count: z.coerce.number().int().min(0).optional(), notes: z.string().max(500).optional() }).passthrough()).max(200).optional(),
    workPerformed: siteText(8000), delaysIssues: siteText(8000), safetyIncidents: siteText(8000), notes: siteText(8000),
};
export const dailyLogCreateSchema = z.object({ jobId: siteUuid, logDate: siteDay, status: dailyLogStatusSchema.optional(), ...dailyLogFields });
/** Status is never set here — publish has its own route; a money role may reopen with `unpublish`. */
export const dailyLogUpdateSchema = z.object({ logDate: siteDay.optional(), ...dailyLogFields }).refine(someOf, 'No fields to update');
export const dailyLogPhotoReorderSchema = z.object({ photoIds: z.array(siteUuid).min(1).max(500) });
export const dailyLogPhotoCaptionSchema = z.object({ caption: z.string().max(500).nullable() });
export const siteVisibleSchema = z.object({ visible: z.boolean().optional() });
export const inspectionCreateSchema = z.object({ templateId: siteUuid, jobId: siteUuidOrNull, assignedTo: siteUuidOrNull, scheduledFor: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()) });
/** Status moves only through answer / submit / sign-off — never by PATCH. */
export const inspectionUpdateSchema = z.object({ jobId: siteUuidOrNull, assignedTo: siteUuidOrNull, scheduledFor: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()), notes: siteText(8000) }).refine(someOf, 'No fields to update');
export const inspectionAnswerSchema = z.object({ answer: z.string().max(2000).nullable().optional(), note: z.string().max(2000).nullable().optional() });
export const inspectionSignatureSchema = z.object({ name: z.string().trim().min(1, 'A name is required with the signature').max(200), imageData: z.string().startsWith('data:image/png;base64,', 'The signature must be a PNG image').max(2_000_000), signedAt: z.string().optional() });
export const defectStatusSchema = z.enum(['open', 'in_progress', 'ready_for_review', 'closed']);
export const defectPrioritySchema = z.enum(['low', 'normal', 'high']);
const defectFields = {
    jobId: siteUuidOrNull, inspectionId: siteUuidOrNull, inspectionItemId: siteUuidOrNull, description: siteText(8000), location: siteText(500),
    priority: defectPrioritySchema.optional(), assignedTo: siteUuidOrNull, supplierId: siteUuidOrNull, dueDate: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()),
};
export const defectCreateSchema = z.object({ title: z.string().trim().min(1, 'A defect title is required').max(500), ...defectFields });
export const defectUpdateSchema = z.object({ title: z.string().trim().min(1).max(500).optional(), status: defectStatusSchema.optional(), ...defectFields }).refine(someOf, 'No fields to update');
export const inspectionSubmitSchema = z.object({
    signature: inspectionSignatureSchema, notes: siteText(8000),
    location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number().optional() }).optional(),
    defects: z.array(defectCreateSchema.partial({ title: true }).extend({ title: z.string().trim().min(1).max(500) })).max(200).optional(),
});
export const inspectionSignoffSchema = z.object({ signature: inspectionSignatureSchema });
export const inspectionScheduleCreateSchema = z.object({ templateId: siteUuid, jobId: siteUuidOrNull, assignedTo: siteUuidOrNull, frequency: z.enum(['daily', 'weekly', 'fortnightly', 'monthly']), nextDate: siteDay });
export const inspectionScheduleUpdateSchema = inspectionScheduleCreateSchema.partial().extend({ active: z.boolean().optional() }).refine(someOf, 'No fields to update');
export const checklistTemplateItemSchema = z.object({ section: siteText(200), question: z.string().trim().min(1, 'Every item needs a question').max(1000), responseType: z.enum(['pass_fail', 'yes_no', 'text', 'number', 'select', 'photo']).optional(), options: z.array(z.string().max(200)).max(50).optional(), required: z.boolean().optional(), photoOnFail: z.boolean().optional() });
export const checklistTemplateCreateSchema = z.object({ name: z.string().trim().min(1, 'Template name is required').max(200), category: z.enum(['safety', 'quality', 'itp', 'handover', 'other']).optional(), description: siteText(2000), requiresSignoff: z.boolean().optional(), items: z.array(checklistTemplateItemSchema).max(500).optional() });
export const checklistTemplateUpdateSchema = checklistTemplateCreateSchema.omit({ items: true }).partial().extend({ status: z.enum(['active', 'archived']).optional() }).refine(someOf, 'No fields to update');
export const checklistTemplateItemsSchema = z.object({ items: z.array(checklistTemplateItemSchema).max(500) });
export const certificationCreateSchema = z.object({ employeeId: siteUuid, name: z.string().trim().min(1, 'A certification name is required').max(200), certType: z.enum(['white_card', 'licence', 'insurance', 'training', 'other']).optional(), number: siteText(100), issuer: siteText(200), issueDate: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()), expiryDate: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()), notes: siteText(2000) });
export const certificationUpdateSchema = certificationCreateSchema.omit({ employeeId: true }).partial().refine(someOf, 'No fields to update');
export const inductionCreateSchema = z.object({ jobId: siteUuidOrNull, employeeId: siteUuidOrNull, personName: z.string().trim().min(1, 'The inducted person’s name is required').max(200), company: siteText(200), inductionDate: siteDay, expiryDate: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()), signature: inspectionSignatureSchema.partial({ imageData: true }).optional(), notes: siteText(2000), inductedByName: siteText(200) });
export const swmsCreateSchema = z.object({ title: z.string().trim().min(1, 'A SWMS title is required').max(300), supplierId: siteUuidOrNull, jobId: siteUuidOrNull, documentId: siteUuidOrNull, highRiskWork: siteText(2000), reviewDate: z.preprocess((v) => (v === '' ? null : v), siteDay.nullable().optional()), notes: siteText(2000) });
export const swmsUpdateSchema = swmsCreateSchema.partial().extend({ status: z.enum(['active', 'superseded', 'archived']).optional() }).refine(someOf, 'No fields to update');
export const incidentTypeSchema = z.enum(['injury', 'near_miss', 'property_damage', 'environmental', 'hazard', 'other']);
export const incidentSeveritySchema = z.enum(['minor', 'first_aid', 'medical_treatment', 'lost_time', 'notifiable']);
const incidentFields = { jobId: siteUuidOrNull, incidentType: incidentTypeSchema.optional(), severity: incidentSeveritySchema.optional(), personName: siteText(200), employeeId: siteUuidOrNull, immediateAction: siteText(4000), correctiveAction: siteText(4000), notifiable: z.boolean().optional() };
export const incidentCreateSchema = z.object({ incidentAt: z.string().datetime({ offset: true }).or(siteDay), description: z.string().trim().min(1, 'A description is required').max(8000), ...incidentFields });
export const incidentUpdateSchema = z.object({ incidentAt: z.string().datetime({ offset: true }).or(siteDay).optional(), description: z.string().trim().min(1).max(8000).optional(), status: z.enum(['open', 'closed']).optional(), ...incidentFields }).refine(someOf, 'No fields to update');
export const toolboxTalkCreateSchema = z.object({ jobId: siteUuidOrNull, talkDate: siteDay, topic: z.string().trim().min(1, 'A topic is required').max(300), notes: siteText(8000), conductedBy: siteUuidOrNull, conductedByName: siteText(200) });
export const toolboxTalkAttendeeSchema = z.object({ employeeId: siteUuidOrNull, name: z.string().trim().min(1, 'The attendee’s name is required').max(200), signature: inspectionSignatureSchema.partial({ imageData: true }).optional() });
export const boqCopyFromSchema = z.object({
    sourceJobNo: boqNo, itemIds: z.array(z.string().min(1).max(64)).min(1).max(5000),
    repriceToCurrent: z.boolean().optional(), zeroQuantities: z.boolean().optional(), copySuppliers: z.boolean().optional(), copyNotes: z.boolean().optional(),
});
export const rfqQuoteSubmitSchema = z.object({
    quoteAmount: z.number().min(0).max(1_000_000_000).optional(),
    quoteNotes: z.string().max(4000).optional(),
    // YYYY-MM-DD only — it's cast to ::date in SQL, so reject anything else here
    // rather than 500 on a bad value (empty string also allowed → treated as null).
    quoteValidUntil: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal('')]).optional(),
    quotedItems: z.array(rfqQuotedItemSchema).max(1000).default([]),
    /** The supplier is not quoting this request (a real answer, recorded as unsuccessful). */
    declined: z.boolean().optional(),
});
/** Query for GET /api/catalogue/price-intelligence. */
export const priceIntelligenceQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    costCentre: z.string().trim().max(20).optional(),
    staleMonths: z.coerce.number().int().min(1).max(60).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
// ---------- AI report customization (report definitions) ----------
// A report DEFINITION is validated structured config the AI may author — never
// SQL or HTML. Field/groupBy/sort names are validated against the server-side
// report-source field catalogue before render; this schema validates shape only.
export const REPORT_COLUMN_FORMATS = ['text', 'currency', 'integer', 'number', 'percent', 'date', 'boolean'];
export const REPORT_PAGE_SIZES = ['A4', 'Letter', 'Legal'];
export const REPORT_ORIENTATIONS = ['portrait', 'landscape'];
export const reportColumnSchema = z.object({
    field: z.string().trim().min(1).max(64),
    label: z.string().trim().max(120).optional(),
    format: z.enum(REPORT_COLUMN_FORMATS).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    width: z.string().trim().max(8).optional(),
});
export const reportBrandingSchema = z.object({
    logo: z.boolean().optional(),
    pageSize: z.enum(REPORT_PAGE_SIZES).optional(),
    orientation: z.enum(REPORT_ORIENTATIONS).optional(),
    fontScale: z.number().min(0.8).max(1.3).optional(),
    footerText: z.string().trim().max(200).optional(),
});
export const reportSortSchema = z.object({
    field: z.string().trim().min(1).max(64),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
/** Body for POST /reports/procurement/order-reconciliation/flag. */
export const orderReconciliationFlagSchema = z.object({
    orderNumbers: z.array(z.string().trim().min(1).max(30)).max(500).optional(),
    jobNo: z.string().trim().min(1).max(20).optional(),
    dryRun: z.boolean().optional(),
});
export const reportDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    base: z.string().trim().min(1).max(64),
    title: z.string().trim().max(160).optional(),
    // Only filter params the chosen base report already accepts; primitive values only.
    filters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    columns: z.array(reportColumnSchema).min(1).max(40),
    groupBy: z.array(z.string().trim().min(1).max(64)).max(2).optional(),
    subtotals: z.boolean().optional(),
    sort: z.array(reportSortSchema).max(3).optional(),
    branding: reportBrandingSchema.optional(),
});
// Tenant-wide branding default (page setup, logo, footer) stored in company metadata.
export const reportBrandingDefaultsSchema = z.object({
    logo: z.boolean().optional(),
    pageSize: z.enum(REPORT_PAGE_SIZES).optional(),
    orientation: z.enum(REPORT_ORIENTATIONS).optional(),
    footerText: z.string().trim().max(200).optional(),
});
// Payload to save a definition (name + the definition itself).
export const saveReportDefinitionSchema = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(['amend', 'new']).optional(),
    origin: z.enum(['ai', 'human']).optional(),
    status: z.enum(['draft', 'published']).optional(),
    definition: reportDefinitionSchema,
});
// ==================== Tier B query blocks (Phase 1) ====================
// Admin-authored (or AI-DRAFTED, Phase 2) read-only SELECT blocks that become
// report sources. The SQL itself is validated by the server-side AST validator
// + run under the dedicated read-only Postgres role — these schemas only police
// the SHAPE of the authoring payload. A block is stored in the tenant table
// `report_query_blocks`; an approved block surfaces as a `qb:<slug>` report source.
export const QUERY_BLOCK_FIELD_TYPES = ['string', 'number', 'currency', 'date', 'percent', 'boolean'];
export const QUERY_BLOCK_PARAM_TYPES = ['string', 'number', 'boolean'];
// A declared OUTPUT column. `name` MUST equal the SELECT alias in the SQL — the
// renderer reads row[name]; a mismatch renders blank (the author sees it in preview).
export const queryBlockColumnSchema = z.object({
    name: z.string().trim().min(1).max(64).regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Column name must be a SQL identifier (letters, digits, underscore).'),
    label: z.string().trim().min(1).max(80),
    type: z.enum(QUERY_BLOCK_FIELD_TYPES).default('string'),
});
// A declared PARAM. Position in the array maps to $1..$n in the SQL.
export const queryBlockParamSchema = z.object({
    name: z.string().trim().min(1).max(64).regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Param name must be a simple identifier.'),
    label: z.string().trim().max(80).optional(),
    type: z.enum(QUERY_BLOCK_PARAM_TYPES).default('string'),
    required: z.boolean().optional(),
});
export const createQueryBlockSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    // The {{schema}}-templated, single-statement read-only SELECT. Hard length cap
    // keeps a pathological payload out of the validator/DB.
    sqlTemplate: z.string().trim().min(1).max(20000),
    columns: z.array(queryBlockColumnSchema).min(1).max(40),
    params: z.array(queryBlockParamSchema).max(16).default([]),
});
export const updateQueryBlockSchema = createQueryBlockSchema.partial();
// Preview an UNSAVED block (or re-run a saved one) during authoring. Param values
// are bound positionally ($1..$n).
export const previewQueryBlockSchema = z.object({
    sqlTemplate: z.string().trim().min(1).max(20000),
    params: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).max(16).default([]),
});
// ── Client portal (homeowner login accounts) ────────────────────────────────
export const portalLoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const portalRefreshSchema = z.object({
    refreshToken: z.string().min(10),
});
export const portalAcceptInviteSchema = z.object({
    token: z.string().min(16),
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
    name: z.string().trim().min(1).max(200).optional(),
});
export const portalForgotPasswordSchema = z.object({
    email: z.string().email(),
});
export const portalResetPasswordSchema = z.object({
    token: z.string().min(16),
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
});
// Staff-side: invite a client to a job's portal.
export const portalInviteSchema = z.object({
    email: z.string().email(),
    name: z.string().trim().min(1).max(200).optional(),
    contactId: z.string().uuid().optional(),
});
// Phase D: client variation response.
export const portalVariationApproveSchema = z.object({
    note: z.string().max(2000).optional(),
});
export const portalVariationDeclineSchema = z.object({
    reason: z.string().trim().min(1, 'A reason is required').max(2000),
});
// ================ Contract claims (money routers) ================
const claimDate = z.string().regex(/^\d{4}-\d{2}-\d{2}/, 'Date must be YYYY-MM-DD');
const claimTemplateStageSchema = z.object({
    stageNumber: z.number().int().min(1).max(999),
    stageName: z.string().trim().min(1).max(200),
    percentage: z.number().min(0).max(100),
    description: z.string().trim().max(500).optional(),
});
const sumsTo100 = (stages) => Math.abs(stages.reduce((n, s) => n + s.percentage, 0) - 100) < 0.05;
export const createClaimTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).optional(),
    stages: z.array(claimTemplateStageSchema).min(1).max(50).refine(sumsTo100, 'Stage percentages must add up to 100'),
});
export const updateClaimTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(1000).optional(),
    stages: z.array(claimTemplateStageSchema).min(1).max(50).refine(sumsTo100, 'Stage percentages must add up to 100').optional(),
    isActive: z.boolean().optional(),
});
export const applyClaimTemplateSchema = z.object({ templateId: z.string().uuid() });
export const updateScheduleStageSchema = z.object({
    stageName: z.string().trim().min(1).max(200).optional(),
    percentage: z.number().min(0).max(100).optional(),
    comments: z.string().trim().max(2000).optional(),
    readyDate: claimDate.optional(),
});
export const stageReadySchema = z.object({ ready: z.boolean() });
export const closeStageSchema = z.object({ tranNo: z.number().int().positive(), comments: z.string().trim().max(2000).optional() });
export const legacyVariationSchema = z.object({
    reference: z.string().trim().max(100).optional(),
    comments: z.string().trim().max(500).optional(),
    amountExGst: z.number().finite(),
    tranDate: claimDate,
    stage: z.string().trim().max(50).optional(),
});
export const contractReceiptSchema = z.object({
    reference: z.string().trim().max(100).optional(),
    amountIncGst: z.number().positive().finite(),
    tranDate: claimDate,
    bank: z.string().trim().max(50).optional(),
    stageNumber: z.number().int().min(1).max(9999).optional(),
});
export const authoriseVariationSchema = z.object({ authorisedBy: z.string().trim().min(1).max(200) });
export const rejectVariationSchema = z.object({ reason: z.string().trim().max(2000).optional() });
export const costCentreClaimItemSchema = z.object({
    costCentreId: z.string().uuid(),
    percentComplete: z.number().min(0).max(100),
    claimAmount: z.number().finite().optional(),
});
export const previewCostCentreClaimSchema = z.object({ items: z.array(costCentreClaimItemSchema).min(1).max(500) });
export const createCostCentreClaimSchema = z.object({
    claimDate: claimDate,
    reference: z.string().trim().max(100).optional(),
    comments: z.string().trim().max(500).optional(),
    items: z.array(costCentreClaimItemSchema).min(1).max(500),
});
// ================ Project task dependencies (Gantt) ================
export const dependencyTypeSchema = z.enum([
    'finish_to_start',
    'start_to_start',
    'finish_to_finish',
    'start_to_finish',
]);
export const addDependencySchema = z.object({
    predecessorId: z.string().uuid(),
    dependencyType: dependencyTypeSchema.default('finish_to_start'),
    lagDays: z.number().int().default(0),
});
export const removeDependencyByEdgeSchema = z.object({
    predecessorId: z.string().uuid(),
});
// ================ Gantt scheduling (auto-schedule + working calendar) ================
export const workingCalendarSchema = z.object({
    workdays: z.array(z.number().int().min(0).max(6)).min(1).optional(),
    daysPerWeek: z.number().int().min(1).max(7).optional(),
    holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(500).optional(),
});
export const projectSettingsSchema = z.object({
    autoSchedule: z.boolean().optional(),
    calendar: workingCalendarSchema.nullable().optional(),
}).refine(o => o.autoSchedule !== undefined || o.calendar !== undefined, { message: 'Nothing to update' });
export const rescheduleSchema = z.object({
    changedTaskId: z.string().uuid(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
}).refine(o => o.dueDate >= o.startDate, { message: 'dueDate must be on or after startDate' });
export const importMsProjectSchema = z.object({
    xml: z.string().min(1, 'XML is required').max(25_000_000, 'File is too large'),
});
// ============================================================================
// SUPPLIER / SUBCONTRACTOR ONBOARDING QUESTIONNAIRE
// ============================================================================
// The public questionnaire autosaves progressively, so the submission schema
// validates SHAPE only (everything optional). Required-ness is enforced by the
// server-side completeness engine, which reports human-readable missing items
// — the same list drives the form's progress bar and the follow-up emails.
/** ABN checksum (ATO modulus-89). Accepts spaces/dashes; requires 11 digits. */
export function isValidAbn(abn) {
    const clean = (abn || '').replace(/\D/g, '');
    if (clean.length !== 11)
        return false;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const digits = clean.split('').map(Number);
    digits[0] -= 1;
    const sum = digits.reduce((acc, d, i) => acc + d * weights[i], 0);
    return sum % 89 === 0;
}
/** WHS Regulation 2017 s291 — High-Risk Construction Work classes (SWMS required). */
export const HIGH_RISK_WORK_CLASSES = [
    { key: 'fall_2m', label: 'Work where a person could fall more than 2 metres' },
    { key: 'telecom_tower', label: 'Work on a telecommunication tower' },
    { key: 'demolition', label: 'Demolition of a load-bearing structure' },
    { key: 'asbestos', label: 'Work involving disturbing asbestos' },
    { key: 'structural_alterations', label: 'Structural alterations needing temporary support' },
    { key: 'confined_space', label: 'Work in or near a confined space' },
    { key: 'shaft_trench', label: 'Work in a shaft or trench deeper than 1.5m or a tunnel' },
    { key: 'explosives', label: 'Use of explosives' },
    { key: 'pressurised_gas', label: 'Work on or near pressurised gas mains or piping' },
    { key: 'chemical_fuel_lines', label: 'Work on or near chemical, fuel or refrigerant lines' },
    { key: 'energised_electrical', label: 'Work on or near energised electrical installations' },
    { key: 'contaminated_atmosphere', label: 'Work in an area with a contaminated or flammable atmosphere' },
    { key: 'tilt_up_precast', label: 'Tilt-up or precast concrete work' },
    { key: 'traffic_corridor', label: 'Work on/near a road, railway or other traffic corridor' },
    { key: 'powered_mobile_plant', label: 'Work in an area with movement of powered mobile plant' },
    { key: 'extreme_temperature', label: 'Work in an area with artificial extremes of temperature' },
    { key: 'drowning_risk', label: 'Work in or near water with a risk of drowning' },
    { key: 'diving', label: 'Diving work' },
];
/** Upload requirement keys the questionnaire tracks documents against. */
export const ONBOARDING_DOCUMENT_REQUIREMENTS = [
    'publicLiability',
    'workersComp',
    'personalAccident',
    'professionalIndemnity',
    'contractorLicence',
    'tradeCertificates',
    'whiteCards',
    'swms',
    'sds',
    'other',
];
const onboardingDateSchema = z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal('')]);
const onboardingAddressSchema = z.object({
    street: z.string().max(200).optional(),
    suburb: z.string().max(100).optional(),
    state: z.string().max(50).optional(),
    postcode: z.string().max(10).optional(),
});
const onboardingContactSchema = z.object({
    name: z.string().max(120).optional(),
    phone: z.string().max(50).optional(),
    email: z.union([z.string().email().max(200), z.literal('')]).optional(),
});
const onboardingInsuranceSchema = z.object({
    insurer: z.string().max(200).optional(),
    policyNumber: z.string().max(100).optional(),
    expiry: onboardingDateSchema.optional(),
    sumInsured: z.number().min(0).max(1_000_000_000).nullish(),
});
export const supplierOnboardingSubmissionSchema = z.object({
    business: z.object({
        legalName: z.string().max(200).optional(),
        tradingName: z.string().max(200).optional(),
        entityType: z.enum(['company', 'sole_trader', 'partnership', 'trust']).nullish(),
        abn: z.string().max(20).optional(),
        /** Sole traders only: declares they operate without an ABN (payer must
         *  apply no-ABN withholding unless a "Statement by a supplier" is held). */
        noAbn: z.boolean().optional(),
        acn: z.string().max(20).optional(),
        gstRegistered: z.boolean().nullish(),
        address: onboardingAddressSchema.optional(),
        phone: z.string().max(50).optional(),
        email: z.union([z.string().email().max(200), z.literal('')]).optional(),
        website: z.string().max(200).optional(),
        trades: z.string().max(500).optional(),
    }).optional(),
    contacts: z.object({
        primary: onboardingContactSchema.optional(),
        accounts: onboardingContactSchema.optional(),
    }).optional(),
    licences: z.object({
        licenceNumber: z.string().max(100).optional(),
        licenceClass: z.string().max(200).optional(),
        licenceExpiry: onboardingDateSchema.optional(),
        certificates: z.string().max(2000).optional(),
        whiteCards: z.array(z.object({
            name: z.string().max(120),
            cardNumber: z.string().max(60).optional(),
        })).max(100).optional(),
    }).optional(),
    insurances: z.object({
        hasEmployees: z.boolean().nullish(),
        publicLiability: onboardingInsuranceSchema.optional(),
        workersComp: onboardingInsuranceSchema.extend({
            wagesDeclared: z.string().max(100).optional(),
        }).optional(),
        personalAccident: onboardingInsuranceSchema.optional(),
        professionalIndemnity: onboardingInsuranceSchema.extend({
            notRequired: z.boolean().optional(),
        }).optional(),
    }).optional(),
    banking: z.object({
        accountName: z.string().max(200).optional(),
        bankName: z.string().max(100).optional(),
        bsb: z.union([z.string().regex(/^\d{3}-?\d{3}$/), z.literal('')]).optional(),
        accountNumber: z.union([z.string().regex(/^\d{4,10}$/), z.literal('')]).optional(),
        remittanceEmail: z.union([z.string().email().max(200), z.literal('')]).optional(),
    }).optional(),
    whs: z.object({
        policyAcknowledged: z.boolean().optional(),
        highRiskWork: z.array(z.string().max(80)).max(30).optional(),
        usesHazardousSubstances: z.boolean().nullish(),
        inductionConfirmed: z.boolean().optional(),
    }).optional(),
    declarations: z.object({
        whiteCards: z.boolean().optional(),
        inductions: z.boolean().optional(),
        licencesProvided: z.boolean().optional(),
        sdsProvided: z.boolean().optional(),
        insuranceAttached: z.boolean().optional(),
        swmsProvided: z.boolean().optional(),
        marketingConsent: z.boolean().optional(),
    }).optional(),
});
/** Body for PUT /supplier-onboarding/:slug/:token (autosave). */
export const supplierOnboardingSaveSchema = z.object({
    submission: supplierOnboardingSubmissionSchema,
});
/** Body for POST /supplier-onboarding/:slug/:token/submit. */
export const supplierOnboardingSubmitSchema = z.object({
    submission: supplierOnboardingSubmissionSchema,
    signature: z.object({
        name: z.string().min(2).max(120),
        role: z.string().max(120).optional(),
    }),
});
/** Body for POST /suppliers/:code/onboarding/send (staff). */
export const supplierOnboardingSendSchema = z.object({
    email: z.union([z.string().email().max(200), z.literal('')]).optional(),
    contactName: z.string().max(120).optional(),
    kind: z.enum(['subcontractor', 'supplier']).optional().default('subcontractor'),
    expiresInDays: z.number().int().min(1).max(90).optional(),
    message: z.string().max(2000).optional(),
});
/** Body for POST /suppliers/:code/onboarding/return (staff, request changes). */
export const supplierOnboardingReturnSchema = z.object({
    reason: z.string().max(2000).optional(),
    extraItems: z.array(z.string().max(300)).max(50).optional(),
});
/** Body for POST /suppliers/:code/onboarding/verify-item (staff ticked/unticked
 *  "verified against the uploaded document" for one reviewed item). */
export const supplierOnboardingVerifyItemSchema = z.object({
    item: z.enum(['publicLiability', 'workersComp', 'personalAccident', 'professionalIndemnity', 'licence', 'whiteCards']),
    verified: z.boolean(),
});
/** Tenant config stored in company_settings.metadata.supplierOnboarding. */
export const supplierOnboardingConfigSchema = z.object({
    plMinimumSum: z.number().min(0).max(1_000_000_000).optional(),
    reminderDays: z.array(z.number().int().min(1).max(60)).max(10).optional(),
    linkExpiryDays: z.number().int().min(1).max(365).optional(),
    introText: z.string().max(4000).optional(),
    safetyPolicyDocumentId: z.string().uuid().nullish(),
    /** Display-only echo of the chosen document's file name. */
    safetyPolicyDocumentName: z.string().max(300).nullish(),
    marketingConsentEnabled: z.boolean().optional(),
});
// ============================================================
// SALES — quotes, leads, DocuSign (module review #8)
// ============================================================
/** Body for POST /quotes/:id/send. The recipient must be a real address —
 *  this route makes the builder's mail server send to it. */
export const quoteSendSchema = z.object({
    email: z.string().trim().email().max(320).optional(),
});
/** Body for POST /quotes/:id/reject and /lost. */
export const quoteReasonSchema = z.object({ reason: z.string().trim().max(2000).optional() });
/** The only metadata keys PATCH /quotes/:id/metadata may write. The tender,
 *  the signature evidence, the DocuSign envelope and the send history are
 *  written by their own routes and never through this one. */
export const QUOTE_SETTING_KEYS = ['pricingMode', 'minPricingPercent'];
export const quoteSettingsSchema = z.object({
    metadata: z
        .object({
        pricingMode: z.enum(['margin', 'markup']).optional(),
        minPricingPercent: z.number().min(0).max(1000).optional(),
    })
        .passthrough(),
});
export const quoteFromCatalogueSchema = z.object({ catalogueItemIds: z.array(z.string().uuid()).min(1).max(500) });
/** Body for the client's POST /quote-view/:slug/:hash/reject. */
export const publicQuoteRejectSchema = z.object({ reason: z.string().trim().max(2000).optional() });
/** Must match LEAD_STAGES in types (a test asserts it). */
export const LEAD_STAGE_KEYS = ['new', 'contacted', 'qualified', 'quoting', 'tender_sent', 'won', 'lost'];
/** The activity types a person may record; stage_change and system are written by the service. */
export const LEAD_ACTIVITY_TYPES = ['note', 'call', 'email', 'meeting', 'task'];
/** Must match LEAD_BID_DECISIONS / LEAD_TENDER_TYPES in types. */
export const LEAD_BID_DECISION_KEYS = ['undecided', 'bid', 'no_bid'];
export const LEAD_TENDER_TYPE_KEYS = ['invited', 'public', 'negotiated'];
const emailOrBlank = z.union([z.literal(''), z.string().trim().email().max(320)]).nullish();
const leadFields = {
    title: z.string().trim().min(1, 'A lead needs a title').max(200),
    contactId: z.string().uuid().nullish(),
    contactName: z.string().trim().max(200).nullish(),
    email: emailOrBlank,
    phone: z.string().trim().max(50).nullish(),
    source: z.string().trim().max(100).nullish(),
    stage: z.enum(LEAD_STAGE_KEYS).optional(),
    estimatedValue: z.coerce.number().min(0).max(1_000_000_000_000).nullish(),
    expectedClose: isoDate.nullish(),
    siteStreet: z.string().trim().max(200).nullish(),
    siteSuburb: z.string().trim().max(100).nullish(),
    siteState: z.string().trim().max(50).nullish(),
    sitePostcode: z.string().trim().max(20).nullish(),
    ownerUserId: z.string().uuid().nullish(),
    notes: z.string().max(10000).nullish(),
    // Tender register
    tenderDueAt: isoDate.nullish(),
    tenderDueTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM').nullish(),
    tenderType: z.enum(LEAD_TENDER_TYPE_KEYS).nullish(),
    bidDecision: z.enum(LEAD_BID_DECISION_KEYS).optional(),
    bidDecisionReason: z.string().trim().max(2000).nullish(),
    probability: z.coerce.number().int().min(0).max(100).nullish(),
    estimatorUserId: z.string().uuid().nullish(),
};
export const leadCreateSchema = z.object(leadFields);
export const leadUpdateSchema = z
    .object({ ...leadFields, convertedJobId: z.string().uuid().nullish(), quoteId: z.string().uuid().nullish() })
    .partial();
export const leadStageSchema = z.object({
    stage: z.enum(LEAD_STAGE_KEYS),
    lostReason: z.string().trim().max(2000).nullish(),
});
export const leadActivityCreateSchema = z.object({
    activityType: z.enum(LEAD_ACTIVITY_TYPES),
    title: z.string().trim().max(300).optional(),
    description: z.string().max(10000).nullish(),
    dueDate: isoDate.nullish(),
});
export const leadActivityUpdateSchema = z.object({
    completed: z.boolean().optional(),
    title: z.string().trim().max(300).optional(),
    description: z.string().max(10000).nullish(),
    dueDate: isoDate.nullish(),
});
// ============================================================
// ACCOUNTING — transactions, payment runs, ledger, matching, integrations, company settings (module review #9)
// ============================================================
const money = z.number().finite().min(-1_000_000_000_000).max(1_000_000_000_000);
const nonNegMoney = z.number().finite().min(0).max(1_000_000_000_000);
const str = (max) => z.string().trim().max(max);
export const transactionCreateSchema = z.object({
    tranType: z.number().int().min(1).max(7),
    jobId: z.string().uuid().nullish(),
    costCentreId: z.string().uuid().nullish(),
    supplierId: z.string().uuid().nullish(),
    contactCode: str(50).nullish(),
    amount: money,
    retention: nonNegMoney.optional(),
    reference: str(100).nullish(),
    comments: str(2000).nullish(),
    memo: str(2000).nullish(),
    tranDate: isoDate,
    dueDate: isoDate.nullish(),
    retentionDueDate: isoDate.nullish(),
    glAccount: z.number().int().nullish(),
    taxCode: str(10).nullish(),
    stageId: z.number().int().nullish(),
    variationOrder: z.number().int().nullish(),
    metadata: z.record(z.unknown()).optional(),
});
/** The type is never editable; the service decides which of these may change after approval. */
export const transactionUpdateSchema = transactionCreateSchema.omit({ tranType: true }).partial();
export const supplierInvoiceCreateSchema = transactionCreateSchema.omit({ tranType: true });
export const receiptRecordSchema = z.object({
    receiptDate: isoDate,
    reference: str(100).nullish(),
    bankAccount: str(100).nullish(),
    comments: str(2000).nullish(),
    totalAmount: nonNegMoney.optional(),
    allocations: z.array(z.object({ claimId: z.string().uuid(), amount: nonNegMoney })).min(1).max(200),
});
export const paymentBatchTranNosSchema = z.object({ tranNos: z.array(z.number().int().positive()).min(1).max(500) });
export const accountingReasonSchema = z.object({ reason: str(2000).optional() });
/** Australian direct-entry (ABA) remitter settings, in the bank's formats. */
export const abaSettingsSchema = z
    .object({
    bankName: z.string().trim().regex(/^[A-Za-z]{3}$/, 'Bank name is the 3-letter code (ANZ, CBA, NAB, WBC)').optional(),
    userBank: z.string().trim().regex(/^\d{3}-\d{3}$/, 'BSB is XXX-XXX').optional(),
    userNumber: z.string().trim().regex(/^\d{1,6}$/, 'User number is 1–6 digits').optional(),
    userName: str(26).optional(),
    apca: z.string().trim().regex(/^\d{1,6}$/, 'APCA id is 1–6 digits').optional(),
    accountNumber: z.string().trim().regex(/^\d{1,9}$/, 'Account number is up to 9 digits').optional(),
    description: str(12).optional(),
    selfBalancing: z.boolean().optional(),
})
    .strict();
export const budgetProgressSchema = z.object({
    items: z.array(z.object({ costCentreId: z.string().uuid(), percentComplete: z.number().min(0).max(100) })).min(1).max(500),
});
export const GL_ACCOUNT_TYPE_KEYS = ['asset', 'liability', 'equity', 'revenue', 'expense', 'commitment'];
export const glAccountCreateSchema = z.object({
    accountCode: str(20).min(1),
    accountName: str(200).min(1),
    accountType: z.enum(GL_ACCOUNT_TYPE_KEYS),
    parentAccountId: z.string().uuid().nullish(),
    isHeader: z.boolean().optional(),
    normalBalance: z.enum(['debit', 'credit']),
    description: str(1000).nullish(),
    sortOrder: z.number().int().optional(),
});
export const glAccountUpdateSchema = z.object({
    accountName: str(200).min(1).optional(),
    description: str(1000).nullish(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const JOURNAL_ENTRY_TYPE_KEYS = [
    'po_commitment', 'po_commitment_release', 'supplier_invoice', 'supplier_payment', 'progress_claim',
    'client_receipt', 'credit_note', 'retention_release', 'journal_adjustment', 'opening_balance',
];
export const journalLineSchema = z.object({
    glAccountCode: str(20).min(1),
    debitAmount: nonNegMoney.optional(),
    creditAmount: nonNegMoney.optional(),
    jobId: z.string().uuid().nullish(),
    costCentreId: z.string().uuid().nullish(),
    supplierId: z.string().uuid().nullish(),
    description: str(500).nullish(),
    reference: str(100).nullish(),
    taxCode: str(10).nullish(),
    taxAmount: money.optional(),
});
export const journalEntryCreateSchema = z.object({
    entryType: z.enum(JOURNAL_ENTRY_TYPE_KEYS).default('journal_adjustment'),
    entryDate: isoDate,
    description: str(500).nullish(),
    sourceTranNo: z.number().int().nullish(),
    sourceOrderId: z.string().uuid().nullish(),
    jobId: z.string().uuid().nullish(),
    costCentreId: z.string().uuid().nullish(),
    supplierId: z.string().uuid().nullish(),
    autoPost: z.boolean().optional(),
    lines: z.array(journalLineSchema).min(2).max(500),
});
/** Built-in posting code → the tenant's own account code. */
export const glMapSchema = z.object({ glMap: z.record(z.string().regex(/^\d{4}$/), str(20)) });
export const ledgerCutoverSchema = z.object({ cutoverDate: isoDate.optional() });
export const ledgerMigrationAutoSchema = z.object({ cutoverDate: isoDate });
export const ledgerPostHistoricalSchema = z.object({ afterDate: isoDate });
export const commitmentReleaseSchema = z.object({ invoiceAmount: z.number().finite().positive().max(1_000_000_000_000) });
export const commitmentCancelSchema = z.object({ reason: str(500).optional() });
export const GRN_STATUS_KEYS = ['received', 'partial', 'damaged', 'returned'];
const qty = z.number().finite().min(0).max(1_000_000_000);
export const invoiceLineMatchSchema = z.object({
    billItemId: z.string().uuid().nullable(),
    priceCode: str(50),
    description: str(500),
    quantityOrdered: qty,
    quantityReceived: qty,
    quantityInvoiced: qty,
    unitPriceOrdered: qty,
    unitPriceInvoiced: qty,
    isAdhoc: z.boolean().optional(),
    grnStatus: z.enum(GRN_STATUS_KEYS).optional(),
});
export const invoiceMatchSchema = z.object({
    tranNo: z.number().int().positive(),
    orderNumber: str(50).min(1),
    lines: z.array(invoiceLineMatchSchema).min(1).max(500),
});
export const VARIANCE_TYPE_KEYS = ['price_over', 'price_under', 'quantity_short', 'quantity_over', 'missing_item', 'adhoc_item'];
export const VARIANCE_ACTION_TYPE_KEYS = ['request_credit', 'accept_no_action', 'accept_update_catalogue', 'accept_update_supplier', 'add_to_catalogue', 'create_backorder', 'apply_retention'];
export const varianceActionSchema = z.object({
    varianceType: z.enum(VARIANCE_TYPE_KEYS),
    actionType: z.enum(VARIANCE_ACTION_TYPE_KEYS),
    newPrice: z.number().finite().positive().max(1_000_000_000).optional(),
    priceLevel: z.number().int().min(1).max(5).optional(),
    updateEstimatePrice: z.boolean().optional(),
    updateSupplierPrice: z.boolean().optional(),
    notes: str(2000).optional(),
    catalogueData: z
        .object({ priceCode: str(50).min(1), description: str(500), costCentreId: z.string().uuid(), unitCode: str(20), estimatePrice: nonNegMoney })
        .optional(),
});
export const retentionApplySchema = z
    .object({ type: z.enum(['percentage', 'fixed']), value: nonNegMoney, reason: str(500) })
    .refine((v) => v.type !== 'percentage' || v.value <= 100, { message: 'Percentage retention is 0–100' });
export const retentionReleaseSchema = z.object({ amount: z.number().finite().positive().max(1_000_000_000_000), releaseDate: isoDate, notes: str(1000).optional() });
export const grnRecordSchema = z.object({
    orderNumber: str(50).min(1),
    grnNumber: str(50).optional(),
    receiptDate: isoDate,
    receivedBy: str(200).optional(),
    items: z.array(z.object({ billItemId: z.string().uuid(), quantityReceived: qty, status: z.enum(GRN_STATUS_KEYS), notes: str(1000).optional(), damageNotes: str(1000).optional() })).min(1).max(500),
});
export const backorderReceiveSchema = z.object({
    items: z.array(z.object({ itemId: z.string().uuid(), quantityReceived: qty, receivedDate: isoDate })).min(1).max(500),
});
export const companySettingsUpdateSchema = z.object({
    companyName: str(200).nullish(),
    companyDetails: str(4000).nullish(),
    acn: str(20).nullish(),
    taxFileNumber: str(20).nullish(),
    gstRate: z.number().min(0).max(100).optional(),
    gstJobCode: str(20).nullish(),
    gstSupplierCode: str(20).nullish(),
    atoCompanyName: str(200).nullish(),
    atoTradingName: str(200).nullish(),
    atoContact: str(200).nullish(),
    atoPhone: str(50).nullish(),
    atoFax: str(50).nullish(),
    atoEmail: emailOrBlank,
    reportLogoPath: str(500).nullish(),
    ordersRequireAuth: z.boolean().optional(),
    ordersAutoApproval: z.boolean().optional(),
    ordersRequireBudget: z.boolean().optional(),
    ordersRequireUserLimit: z.boolean().optional(),
    autoOrderApprovalValue: nonNegMoney.nullish(),
    nextClaimNumber: z.number().int().min(1).optional(),
    nextOrderNumber: z.number().int().min(1).optional(),
    writePriceLog: z.boolean().optional(),
    useSecurityLevel: z.boolean().optional(),
});
export const INTEGRATION_PLATFORMS = ['quickbooks', 'xero', 'myob'];
export const INTEGRATION_SYNC_TYPES = ['INV', 'PAY', 'CLM', 'REC', 'BCH', 'JNL', 'ADJ', 'RET', 'ORD'];
export const integrationSettingsSchema = z.object({
    platform: z.enum(INTEGRATION_PLATFORMS).nullish(),
    autoSync: z.boolean().optional(),
    syncTypes: z.array(z.enum(INTEGRATION_SYNC_TYPES)).max(10).optional(),
});
export const glMappingSaveSchema = z.object({
    localAccountCode: str(20).min(1),
    localAccountName: str(200).default(''),
    remoteAccountId: str(100).min(1),
    remoteAccountName: str(200).default(''),
    syncEnabled: z.boolean().default(true),
});
/** Apply several mappings at once (the "Apply suggestions" button). */
export const glMappingBulkSaveSchema = z.object({ mappings: z.array(glMappingSaveSchema).min(1).max(200) });
/** Create the missing platform account for a Costed account and map it in one step. */
export const remoteAccountCreateSchema = z.object({
    localAccountCode: str(20).min(1),
    name: str(100).min(1).optional(),
});
/** Posting screen: which queue to list, and which documents to post / send. */
export const postingQueueQuerySchema = z.object({
    queue: z.enum(['to_post', 'to_send', 'blocked', 'sent', 'all']).default('to_post'),
    tranType: z.coerce.number().int().optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    limit: z.coerce.number().int().min(1).max(2000).optional(),
});
export const postingActionSchema = z.object({ tranNos: z.array(z.number().int().positive()).max(200).default([]), orderIds: z.array(z.string().uuid()).max(200).default([]) }).refine((b) => b.tranNos.length + b.orderIds.length > 0, 'Nothing to act on');
export const postingStatusQuerySchema = z.object({ tranNos: z.string().min(1).max(4000) });
/** Settings → Posting rules (owner/admin). */
export const postingRulesUpdateSchema = z.object({
    mode: z.object({ INV: z.enum(['auto', 'queue']).optional(), CRN: z.enum(['auto', 'queue']).optional(), CLM: z.enum(['auto', 'queue']).optional(), REC: z.enum(['auto', 'queue']).optional() }).optional(),
    direction: z.object({ orders: z.enum(['off', 'push']).optional(), bills: z.enum(['costed', 'platform']).optional() }).optional(),
    lockDate: isoDate.nullable().optional(),
});
/** Pull payments back from the connected platform (dry run = report only). */
export const postingPullSchema = z.object({ dryRun: z.boolean().default(false) });
/** Import the platform's chart into Costed's ledger — a dry run unless told otherwise. */
export const chartImportSchema = z.object({
    dryRun: z.boolean().default(true),
    mapToSelf: z.boolean().default(true),
    applyRoleMap: z.boolean().default(true),
});
// ============================================================
// REPORTS — designer templates, categories (module review #10)
// ============================================================
/** A saved banded-report template lives in company_settings.metadata — bounded so
 *  the settings row (read on almost every request) cannot be grown without limit. */
export const RW_TEMPLATE_MAX_BYTES = 512_000;
export const RW_TEMPLATE_MAX_COUNT = 200;
export const rwTemplateSaveSchema = z.object({
    id: z.string().trim().max(80).optional(),
    kind: z.string().trim().min(1).max(64).regex(/^[a-z0-9_.-]+$/i, 'kind is a short identifier'),
    name: z.string().trim().min(1).max(120),
    definition: z.record(z.unknown()),
    status: z.enum(['draft', 'published']).optional(),
    origin: z.enum(['ai', 'human']).optional(),
});
export const reportCategoriesSchema = z.object({
    categories: z.array(z.object({ id: z.string().trim().min(1).max(64), label: z.string().trim().max(60) })).max(100).optional(),
    assignments: z.record(z.string().max(64), z.string().max(64)).optional(),
    order: z.array(z.string().max(64)).max(200).optional(),
});
/** Client-built HTML rendered to PDF: bounded, JavaScript never runs in it. */
export const REPORT_HTML_MAX_CHARS = 2_000_000;
export const generatePdfSchema = z.object({
    html: z.string().min(1).max(REPORT_HTML_MAX_CHARS, `HTML is limited to ${REPORT_HTML_MAX_CHARS / 1_000_000} MB`),
    options: z
        .object({
        format: z.enum(['A4', 'A3', 'Letter', 'Legal']).optional(),
        landscape: z.boolean().optional(),
        footerText: z.string().max(300).optional(),
        filename: z.string().max(120).optional(),
        margin: z.object({ top: z.string().max(10).optional(), right: z.string().max(10).optional(), bottom: z.string().max(10).optional(), left: z.string().max(10).optional() }).optional(),
        printBackground: z.boolean().optional(),
        scale: z.number().min(0.1).max(2).optional(),
        displayHeaderFooter: z.boolean().optional(),
        headerTemplate: z.string().max(4000).optional(),
        footerTemplate: z.string().max(4000).optional(),
    })
        .passthrough()
        .optional(),
});
export const generateExcelSchema = z.object({
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().max(300).optional(),
    columns: z.array(z.object({ key: z.string().max(80), header: z.string().max(120) }).passthrough()).min(1).max(100),
    data: z.array(z.record(z.unknown())).max(50_000),
    filename: z.string().max(120).optional(),
    sheetName: z.string().max(31).optional(),
    includeTotals: z.boolean().optional(),
    totalColumns: z.array(z.string().max(80)).max(100).optional(),
});
// ============================================================
// SETTINGS — user configuration rows (module review #11)
// ============================================================
export const userConfigSchema = z.object({
    userName: z.string().trim().min(1).max(100),
    displayName: z.string().trim().max(200).nullish(),
    email: z.union([z.literal(''), z.string().trim().email().max(320)]).nullish(),
    securityLevel: z.number().int().min(0).max(9).optional(),
    orderLimit: z.number().finite().min(0).max(1_000_000_000_000).nullish(),
    budgetLimit: z.number().finite().min(0).max(1_000_000_000_000).nullish(),
    variationLimit: z.number().finite().min(0).max(1_000_000_000_000).nullish(),
    etsLimit: z.number().finite().min(0).max(1_000_000_000_000).nullish(),
    usePassword: z.boolean().optional(),
    canSetup: z.boolean().optional(),
    canPost: z.boolean().optional(),
    approvalEmail: z.boolean().optional(),
    contactCode: z.string().trim().max(20).nullish(),
    metadata: z.record(z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Platform Admin portal bodies (module review #14). Every admin write used to
// take req.body raw: statuses were free strings, numbers unbounded, ids
// unchecked. The vocabularies here include the values the system itself writes
// (pending_verification, deleted, trial_expired) so the portal can filter them.
// ---------------------------------------------------------------------------
export const ADMIN_LEAD_STATUSES = ['lead', 'pending_verification', 'qualified', 'trial', 'follow_up', 'active', 'suspended', 'cancelled', 'deleted'];
export const ADMIN_SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'trial_expired', 'cancelled', 'incomplete', 'paused'];
export const ADMIN_FEEDBACK_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
export const ADMIN_BILLING_INTERVALS = ['monthly', 'annual'];
const httpsUrl = z.string().trim().max(500).refine((s) => /^https:\/\/[^\s]+$/i.test(s), 'Must be an https:// URL');
const optionalText = (max) => z.string().trim().max(max).nullish();
const uuid = z.string().uuid();
export const adminLeadCreateSchema = z.object({
    companyName: z.string().trim().min(1).max(255),
    contactName: optionalText(200),
    email: z.string().trim().email().max(255).nullish(),
    phone: optionalText(50),
    country: optionalText(100),
    countryCode: optionalText(10),
    timezone: optionalText(64),
    status: z.enum(ADMIN_LEAD_STATUSES).optional(),
    leadSource: optionalText(100),
    referrerUrl: optionalText(2000),
    salespersonId: uuid.nullish(),
    metadata: z.record(z.unknown()).optional(),
}).strict();
export const adminLeadUpdateSchema = adminLeadCreateSchema.partial().extend({
    distributorId: uuid.nullish(),
    resellerId: uuid.nullish(),
    demoScheduledAt: z.coerce.date().nullish(),
    nextFollowUpAt: z.coerce.date().nullish(),
    isPhoneInvalid: z.boolean().optional(),
    isUnsubscribed: z.boolean().optional(),
    requestedDelete: z.boolean().optional(),
    hasTestAccess: z.boolean().optional(),
    trainingHoursRemaining: z.number().min(0).max(999).optional(),
    notes: z.string().max(20_000).nullish(),
    workspaceUrl: httpsUrl.nullish(),
}).strict();
export const adminLeadBulkUpdateSchema = z.object({
    ids: z.array(uuid).min(1).max(100),
    status: z.enum(ADMIN_LEAD_STATUSES).optional(),
    salespersonId: uuid.nullable().optional(),
}).refine((b) => b.status !== undefined || b.salespersonId !== undefined, 'No update fields provided');
export const adminLeadNoteSchema = z.object({
    content: z.string().min(1).max(64_000, 'A note is limited to 64 KB — attach large images as documents'),
    noteType: z.enum(['note', 'call', 'email', 'meeting', 'training', 'system']).optional(),
});
export const adminSubscriptionUpdateSchema = z.object({
    planId: uuid.optional(),
    priceId: uuid.optional(),
    billingEmail: z.string().trim().email().max(255).optional(),
    seatCount: z.number().int().min(1).max(1000).optional(),
    storageAddonGb: z.number().int().min(0).max(10_000).optional(),
    status: z.enum(ADMIN_SUBSCRIPTION_STATUSES).optional(),
    couponCode: z.string().trim().max(50).optional(),
}).strict();
export const adminExtendTrialSchema = z.object({ days: z.number().int().min(1).max(90).optional() });
export const adminCancelSubscriptionSchema = z.object({ immediately: z.boolean().optional() });
export const adminChangePlanSchema = z.object({ planId: uuid, billingInterval: z.enum(ADMIN_BILLING_INTERVALS).optional() });
export const adminPlanCreateSchema = z.object({
    name: z.string().trim().min(1).max(100),
    tierLevel: z.number().int().min(0).max(20),
    storageLimitGb: z.number().int().min(0).max(100_000),
    features: z.array(z.string().trim().max(200)).max(50).optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
    monthlyPrice: z.number().int().min(0).max(10_000_000).optional(),
    annualPrice: z.number().int().min(0).max(10_000_000).optional(),
}).strict();
export const adminPlanUpdateSchema = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    storageLimitGb: z.number().int().min(0).max(100_000).optional(),
    features: z.array(z.string().trim().max(200)).max(50).optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
}).strict();
export const adminPlanPriceSchema = z.object({
    billingInterval: z.enum(ADMIN_BILLING_INTERVALS),
    unitAmount: z.number().int().min(0).max(10_000_000),
    currency: z.string().trim().length(3).toLowerCase().optional(),
});
export const adminPriceStatusSchema = z.object({ isActive: z.boolean() });
export const adminStorageAddonSchema = z.object({
    monthlyAmount: z.number().int().min(0).max(1_000_000).optional(),
    annualAmount: z.number().int().min(0).max(10_000_000).optional(),
    gbPerBlock: z.number().int().min(1).max(10_000).optional(),
});
export const adminCouponCreateSchema = z.object({
    code: z.string().trim().min(3).max(40).regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, - and _ only'),
    couponType: z.enum(['percent_off', 'amount_off', 'extra_months']),
    percentOff: z.number().min(1).max(100).optional(),
    amountOff: z.number().int().min(1).max(10_000_000).optional(),
    extraMonths: z.number().int().min(1).max(24).optional(),
    currency: z.string().trim().length(3).toLowerCase().optional(),
    maxRedemptions: z.number().int().min(1).max(100_000).optional(),
    expiresAt: z.coerce.date().optional(),
    redemptionWindowHours: z.number().int().min(1).max(8760).optional(),
    subscriptionOnly: z.boolean().optional(),
}).strict();
export const adminCouponUpdateSchema = z.object({
    maxRedemptions: z.number().int().min(1).max(100_000).nullish(),
    expiresAt: z.coerce.date().nullish(),
    isActive: z.boolean().optional(),
}).strict();
export const adminCouponCodeSchema = z.object({ code: z.string().trim().min(1).max(40) });
export const adminTrainingAdjustSchema = z.object({
    hours: z.number().min(-100).max(100).refine((h) => h !== 0, 'Hours cannot be zero'),
    reason: z.string().trim().min(1).max(500),
});
export const adminTestCheckoutSchema = z.object({
    planType: z.enum(['pro', 'enterprise', 'core']).optional(),
    priceId: z.string().trim().regex(/^price_[A-Za-z0-9]+$/).optional(),
    embedded: z.boolean().optional(),
    billingInterval: z.enum(ADMIN_BILLING_INTERVALS).optional(),
    includeTraining: z.boolean().optional(),
    subscriptionQuantity: z.number().int().min(1).max(100).optional(),
    trainingQuantity: z.number().int().min(1).max(100).optional(),
});
/** Provisioning needs someone to OWN the workspace: the lead's email, unless a different contact is given. */
export const adminProvisionTenantSchema = z.object({
    startTrial: z.boolean().optional(),
    planId: uuid.optional(),
    contactEmail: z.string().trim().toLowerCase().email().max(254).optional(),
});
export const adminFeedbackUpdateSchema = z.object({
    status: z.enum(ADMIN_FEEDBACK_STATUSES).optional(),
    adminNotes: z.string().max(20_000).nullish(),
});
export const adminUrlPreviewSchema = z.object({ url: z.string().trim().min(1).max(2048).url() });
